import { getSegment } from "./segment/helpers.js";
import {
  ASSESSMENT_NAME,
  CELLS,
  CONFIRM_BY_CELL,
  DISCLAIMER,
  DOMAINS,
  DOMAIN_CANON,
  DOMAIN_OVERRIDES,
  FSI_DESCRIPTOR,
  MATURITY_LABELS,
  SLOTS,
  SPINE,
  readinessTiers,
  dated,
} from "./lib/apra-assessment-data.js";

/*!
 * Readiness Assessment (multi-geo / multi-industry) - PagerDuty WordPress build.
 * Build: 085c12d* 2026-09-29 13:00 UTC
 * hand-edit; change the canonical file and regenerate.
 */
("use strict");
(function () {
  var MKTO_ENDPOINT = "/api/mkto2.php";
  var MKTO_PROGRAM_NAME = "APAC-Survey-APRA Assessment 2026";

  var MKTO_DEST = { endpoint: MKTO_ENDPOINT, programName: MKTO_PROGRAM_NAME };
  var MKTO_BY_CELL = {
    "au-fsi": MKTO_DEST,
    "au-fsi-lite": MKTO_DEST,
    "au-telco": MKTO_DEST,
    "au-ecom": MKTO_DEST,
    "au-digital": MKTO_DEST,
    "au-digital-lite": MKTO_DEST,
    "au-generic": MKTO_DEST,
    "sg-fsi": MKTO_DEST,
    "sg-telco": MKTO_DEST,
    "sg-ecom": MKTO_DEST,
    "sg-digital": MKTO_DEST,
    "sg-generic": MKTO_DEST,
    "nz-fsi": MKTO_DEST,
    "nz-telco": MKTO_DEST,
    "nz-ecom": MKTO_DEST,
    "nz-digital": MKTO_DEST,
    "nz-generic": MKTO_DEST,
    "hk-fsi": MKTO_DEST,
    "hk-telco": MKTO_DEST,
    "hk-ecom": MKTO_DEST,
    "hk-digital": MKTO_DEST,
    "hk-generic": MKTO_DEST,
    "my-fsi": MKTO_DEST,
    "my-telco": MKTO_DEST,
    "my-ecom": MKTO_DEST,
    "my-digital": MKTO_DEST,
    "my-generic": MKTO_DEST,
    "th-fsi": MKTO_DEST,
    "th-telco": MKTO_DEST,
    "th-ecom": MKTO_DEST,
    "th-digital": MKTO_DEST,
    "th-generic": MKTO_DEST,
    "kr-fsi": MKTO_DEST,
    "kr-telco": MKTO_DEST,
    "kr-ecom": MKTO_DEST,
    "kr-digital": MKTO_DEST,
    "kr-generic": MKTO_DEST,
    "other-fsi": MKTO_DEST,
    "other-telco": MKTO_DEST,
    "other-ecom": MKTO_DEST,
    "other-digital": MKTO_DEST,
    "other-generic": MKTO_DEST,
    default: MKTO_DEST,
  };
  function mktoDest() {
    return MKTO_BY_CELL[appState.cell] || MKTO_BY_CELL["default"];
  }
  var LEAD_SOURCE = "Operational Readiness Assessment";
  var RESULTS_PATH = "/apra/assessment-quiz/results"; // mirrors the live quiz's own results-link convention
  var SHEETS_ENDPOINT =
    "https://script.google.com/macros/s/AKfycbyiAdwbbOBcYRjFF8Ej0rZESo5VHz22CwVH6S5fKZQmuABNdRXjrrBcr7HSX2HQ15rWIw/exec";

  var DEFAULT_GEO = "au";

  var DEBUG = false;
  var DEBUG_SHEETS_LIVE = false; // debug tooling does not ship in the WP build

  function debugLog() {}
  function renderDebugPanel() {}
  function renderDebugSkip() {}
  function debugSkip() {}
  function debugPrefillLeadForm() {}

  var HOME = {
    "au-fsi": {
      h1: "AI is adding pressure to APRA-regulated operations. How ready is your organisation?",
      sub: "Answer a short set of scenario questions and get a personalised scorecard like the sample below - a scorecard showing potential gaps across critical operations, incident response, breach reporting, evidence trails and emerging AI risk controls. Answer five questions for an initial result, or continue for the full assessment.",
      chips: ["AI readiness", "CPS 230", "CPS 234", "SOCI Act", "Privacy Act"],
    },
    "au-fsi-lite": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "AI governance expectations and privacy clocks don't stop at the prudential perimeter. Run the check and get a personalised scorecard like the sample below - five questions for an initial result, or continue for the full assessment.",
      chips: [
        "AI readiness",
        "Licence obligations",
        "Breach reporting",
        "Privacy Act",
      ],
    },
    "au-telco": {
      h1: "How outage-ready is your network operation?",
      sub: dated(
        "2026-10-04",
        "Maturity indicator 1 falls due on 3 October 2026, the triple-zero rules carry penalties up to $30 million, and AI is now used within operational workflows. Check your current position - a personalised scorecard like the sample below, in a few minutes.",
        "Maturity indicator 1 fell due on 3 October 2026 - where did you land? The triple-zero rules carry penalties up to $30 million, and AI is now used within operational workflows. Check your current position - a personalised scorecard like the sample below, in a few minutes.",
      ),
      chips: [
        "SOCI / TSRMP",
        "ECS Rules",
        "Outage Comms Standard",
        "AI readiness",
      ],
    },
    "au-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Find out whether your response keeps up with your losses: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "CPS 230 pushes your FSI customers' obligations into your contracts - and their regulators are now asking about AI suppliers. See whether your incident response stands up: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "CPS 230 flow-down",
        "Customer evidence (SOC 2 / ISO 27001)",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-digital-lite": {
      h1: "Your platform may be critical infrastructure now. How incident-ready are you?",
      sub: "Data storage and processing is a SOCI sector - meet the critical-asset threshold and CIRMP obligations and incident reporting follow, and AI is now used within operational workflows. See whether your response stands up: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "SOCI (data storage & processing)",
        "Customer evidence",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Australian organisations: operational resilience with an AI readiness reading, mapped to your Privacy Act and Cyber Security Act obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Privacy Act",
      ],
    },
    "sg-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "MAS expects notification within one hour of discovering an incident, and has set out AI model-risk expectations. Check your current position as the updated ORM guidelines land - a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "AI readiness",
        "MAS TRM",
        "Incident notification (1-hour)",
        "PDPA",
      ],
    },
    "sg-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "The Cybersecurity Act gives CII owners two hours to report an incident - and AI is now used within operational workflows. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Cybersecurity Act (CII)",
        "IMDA resilience codes",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "The draft Digital Infrastructure Bill proposes resilience and incident-reporting obligations for major data centre and cloud providers - and MAS outsourcing rules already flow through your FSI contracts. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Digital Infrastructure Bill (draft)",
        "MAS outsourcing flow-down",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Singapore organisations: operational resilience with an AI readiness reading, mapped to your PDPA obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "nz-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "The Reserve Bank already asks you to self-assess against its cyber resilience guidance - and AI is making the questions harder. This is a structured way to do both: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "AI readiness",
        "RBNZ Cyber Resilience Guidance",
        "Incident reporting (72-hour)",
        "Privacy Act 2020",
      ],
    },
    "nz-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "When networks fail, 111 access and customer communication are the first questions asked - and AI is now used within operational workflows. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: ["TICSA", "111 Contact Code", "AI readiness", "Privacy Act 2020"],
    },
    "nz-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "The Reserve Bank's outsourcing policy writes prescribed terms into large banks' vendor contracts - and their regulators are asking about AI suppliers. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Outsourcing flow-down (BS11)",
        "Customer evidence",
        "AI readiness",
        "Privacy Act 2020",
      ],
    },
    "nz-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "Privacy Act 2020",
      ],
    },
    "nz-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for New Zealand organisations: operational resilience with an AI readiness reading, mapped to your Privacy Act 2020 obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Privacy Act 2020",
      ],
    },
    "hk-fsi": {
      h1: dated(
        "2027-06-01",
        "The OR-2 deadline has passed. Could you evidence your resilience?",
        "OR-2 is business as usual now. Could you still evidence it?",
      ),
      sub: dated(
        "2027-06-01",
        "The HKMA required banks to be operationally resilient by 31 May 2026 - and Cap. 653 now puts 12-hour incident clocks on critical infrastructure. Check your current position with a personalised scorecard like the sample below. Answer five questions for an initial result, or continue for the full assessment.",
        "The HKMA expects authorised institutions to be operationally resilient under SPM OR-2 - and Cap. 653 puts 12-hour incident clocks on critical infrastructure. Check your current position with a personalised scorecard like the sample below. Answer five questions for an initial result, or continue for the full assessment.",
      ),
      chips: [
        "HKMA OR-2",
        "C-RAF / TM-E-1",
        "Cap. 653 (12-hour)",
        "PDPO",
        "AI readiness",
      ],
    },
    "hk-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "Communications is a designated sector under Cap. 653 - serious incidents reported within 12 hours, others within 48 - and AI is now used within operational workflows. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: ["Cap. 653 (CII)", "OFCA obligations", "AI readiness", "PDPO"],
    },
    "hk-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPO",
      ],
    },
    "hk-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "HKMA operational-resilience and outsourcing expectations flow through your bank customers' contracts - and Cap. 653 reaches designated digital infrastructure directly. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "HKMA outsourcing flow-down",
        "Customer evidence",
        "AI readiness",
        "PDPO",
      ],
    },
    "hk-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Hong Kong organisations: operational resilience with an AI readiness reading, mapped to Cap. 653 and your PDPO obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPO",
      ],
    },
    "my-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "BNM's RMiT sets some of APAC's most prescriptive technology-risk rules, and the Cyber Security Act has critical entities notifying NACSA immediately, with particulars inside six hours. Check your current position - a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "AI readiness",
        "BNM RMiT",
        "Cyber Security Act (6-hour)",
        "PDPA",
      ],
    },
    "my-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "Communications is a critical sector under the Cyber Security Act 2024 - incidents go to NACSA immediately, with particulars within six hours - and AI is now used within operational workflows. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Cyber Security Act (NCII)",
        "MCMC obligations",
        "AI readiness",
        "PDPA",
      ],
    },
    "my-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "my-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "Your enterprise customers' obligations flow down through contracts - and critical-sector customers under the Cyber Security Act expect incident evidence on a six-hour clock. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: ["NCII flow-down", "Customer evidence", "AI readiness", "PDPA"],
    },
    "my-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Malaysian organisations: operational resilience with an AI readiness reading, mapped to your PDPA obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "th-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "The Bank of Thailand's IT risk guidelines set the supervisory bar, the Cybersecurity Act gives critical infrastructure 24 hours to report, and the PDPA runs a 72-hour breach clock. Check your current position - a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "AI readiness",
        "BOT IT risk guidelines",
        "Cybersecurity Act (24-hour)",
        "PDPA",
      ],
    },
    "th-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "Telecommunications is critical infrastructure under the Cybersecurity Act - the initial incident report is due to the NCSA within 24 hours - and AI is now used within operational workflows. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Cybersecurity Act (CII)",
        "NBTC obligations",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "Your enterprise customers answer to the Bank of Thailand and the NCSA - their obligations flow into your contracts, and their auditors want evidence of YOUR incident response. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Customer evidence",
        "Cybersecurity Act flow-down",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Thai organisations: operational resilience with an AI readiness reading, mapped to your PDPA obligations. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "kr-fsi": {
      h1: "AI is now law in Korea. Is your resilience keeping up?",
      sub: "The AI Basic Act took effect in January 2026, e-finance incidents carry their own reporting regime, and PIPA gives you 72 hours on a breach. Check your current position - a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "AI Basic Act",
        "E-finance incident reporting",
        "PIPA (72-hour)",
        "AI readiness",
      ],
    },
    "kr-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "Korea wrote network-stability obligations into law after high-profile outages, and the AI Basic Act now reaches the operational loop too. Check your current position: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Network stability obligations",
        "AI Basic Act",
        "PIPA (72-hour)",
        "AI readiness",
      ],
    },
    "kr-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. Check where your response currently sits: a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI Basic Act",
        "PIPA",
      ],
    },
    "kr-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "Your enterprise customers' obligations flow down through contracts - and with the AI Basic Act in force, the AI in your platform is part of what their auditors ask about. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: ["Customer evidence", "AI Basic Act", "PIPA", "AI readiness"],
    },
    "kr-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Built for Korean organisations: operational resilience with an AI readiness reading, mapped to PIPA and the AI Basic Act. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI Basic Act",
        "24/7 coverage",
        "PIPA",
      ],
    },

    "other-fsi": {
      h1: "AI is now inside critical financial operations. Is your resilience keeping up?",
      sub: "Financial regulators are converging on the same test wherever you operate: know your critical operations, detect fast, notify on a clock, keep the evidence - and govern the AI inside all of it. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "Breach response",
        "Evidence & audit",
        "AI readiness",
      ],
    },
    "other-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "When a network fails, the fix and the customer communication are judged separately - both on the clock, with emergency-call obligations attached. And AI is now used within operational workflows. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Service continuity",
        "Emergency calls",
        "Outage comms",
        "AI readiness",
      ],
    },
    "other-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and the fault is often a payment provider, not your stack. The test is how fast you know, and how fast you can prove it's them, not you. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "Breach response",
        "AI readiness",
      ],
    },
    "other-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "Enterprise customers' obligations flow down through contracts, their auditors want evidence of YOUR incident response, and their regulators are now asking about AI suppliers. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Customer evidence",
        "Contractual obligations",
        "Breach response",
        "AI readiness",
      ],
    },
    "other-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Operational resilience with an AI readiness reading: how fast you detect, escalate and recover - and whether you can prove it afterwards. Get a personalised scorecard like the sample below, in a few minutes.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Evidence & automation",
      ],
    },
    default: {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Whatever your market and industry, it comes down to how fast you know something's wrong, how fast the right people respond, and whether you can prove it afterwards. Two quick questions match the check to your regulators, your clocks and your obligations - then you get a personalised scorecard like the sample below. A few minutes.",
      chips: [
        "Detection & response",
        "AI readiness",
        "24/7 coverage",
        "Evidence & automation",
      ],
    },
  };

  var HOME_PARTIAL_GEO = {
    au: {
      h1: "Australian regulators now measure resilience in hours, not quarters. How ready are you?",
      sub: "Whatever industry you're in, Australia now has a clock for you: $30 million triple-zero rules for telcos, 12-hour cyber reporting for critical infrastructure, Privacy Act penalties in the hundreds of millions - and CPS 230 if you're in financial services. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    sg: {
      h1: "In Singapore the incident clocks run in hours. Does your response?",
      sub: "Singapore counts incident response in hours: critical infrastructure owners get two, MAS expects notification within one hour from financial institutions, and notifiable PDPA breaches must reach the PDPC within three days. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the clocks that apply to you. A few minutes.",
    },
    nz: {
      h1: "New Zealand's regulators are asking harder resilience questions. This is a structured way to answer.",
      sub: "New Zealand's regulators are asking harder questions across the board: Privacy Act 2020 notification when a breach risks serious harm, RBNZ cyber self-assessments and 72-hour reporting for financial services, 111 continuity obligations for telcos - and AI now in the operational loop. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    hk: {
      h1: "Hong Kong now measures resilience in hours. How ready are you?",
      sub: "The OR-2 operational-resilience deadline has passed for banks, Cap. 653 gives critical infrastructure 12 hours to report serious incidents, and AI is now used within operational workflows. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    my: {
      h1: "Malaysia now counts incident response in hours. How ready are you?",
      sub: "The Cyber Security Act has critical sectors notifying NACSA immediately, with incident particulars inside six hours, PDPA breaches run on a 72-hour clock, and BNM holds financial institutions to RMiT. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    th: {
      h1: "Thailand's incident clocks are already running. How ready are you?",
      sub: "Critical infrastructure gets 24 hours to report under the Cybersecurity Act, the PDPA runs a 72-hour breach clock, and the Bank of Thailand sets the bar for financial services. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    kr: {
      h1: "Korea put AI into law. How ready are your operations?",
      sub: "The AI Basic Act is in force, PIPA runs a 72-hour breach clock, and e-finance incidents carry their own reporting regime. Tell us your industry in the first question and get a personalised scorecard like the sample below, mapped to the obligations that are actually yours. A few minutes.",
    },
    other: {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Wherever you operate, the test is the same: how fast you know, how fast you respond, and whether you can prove it afterwards. Tell us your industry in the first question and get a personalised scorecard like the sample below. A few minutes.",
    },
  };

  var AI_FOCUS_HOME = {
    kr: {
      h1: "Korea put AI into law. How ready are your operations?",
      sub: "The AI Basic Act is in force: high-impact AI carries statutory duties on risk management, human oversight and user notification. This check scores your AI readiness alongside the obligations you already carry - a personalised scorecard in a few minutes.",
    },
    hk: {
      h1: "AI is in your operations. Hong Kong's regulators have noticed.",
      sub: "The PCPD has published AI governance guidance, and the HKMA has set supervisory expectations for banks using generative AI. When a model misbehaves inside a critical operation, someone has to notice in time. This check scores that alongside the obligations you already carry - a few minutes.",
    },
    default: {
      h1: "AI is in your operations. Is it in your incident response?",
      sub: "Regulators are moving from principles to expectations on AI: who owns each use case, what happens when a model misbehaves inside a critical operation, and how quickly a human can take over. This check scores your AI readiness alongside the obligations you already carry - a personalised scorecard in a few minutes.",
    },
  };

  var HOME_AU_FSI_OPEN = {
    h1: "AI is adding pressure to financial services operations. How ready is your organisation?",
    sub: "Banks, insurers and super funds answer to APRA; fintechs and payments providers carry licence and privacy clocks of their own. One quick question sorts out which rules apply to you, then a short set of scenario questions gets you a personalised scorecard like the sample below. A few minutes.",
  };
  var HOME_PARTIAL_INDUSTRY = {
    fsi: {
      h1: "AI is now inside critical financial operations. Is your resilience keeping up?",
      sub: "Financial regulators across APAC are converging on the same test: know your critical operations, detect fast, notify on a clock, keep the evidence - and govern the AI inside all of it. Pick your market in the first question and get a personalised scorecard like the sample below, mapped to your regulator. A few minutes.",
    },
    "fsi-lite": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "Fintechs and payments providers sit inside everyone else's critical path - your customers' regulators, AML obligations and privacy clocks all land on your incident response. Pick your market in the first question and get a personalised scorecard like the sample below, mapped to your obligations. A few minutes.",
    },
    telco: {
      h1: "How outage-ready is your network operation?",
      sub: "When a network fails, the fix and the customer communication are judged separately - both on the clock, with emergency-call obligations and penalties attached. And AI is now used within operational workflows. Pick your market in the first question and get a personalised scorecard like the sample below, mapped to your rules. A few minutes.",
    },
    ecom: {
      h1: "How much does a bad hour cost you?",
      sub: "When checkout breaks at peak, every minute has a dollar figure - and the fault is often a payment provider, not your stack. The test is how fast you know, and how fast you can prove it's them, not you. Pick your market in the first question and get a personalised scorecard like the sample below, including your market's privacy rules. A few minutes.",
    },
    digital: {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "Enterprise customers' obligations flow down through contracts, their auditors want evidence of YOUR incident response, and their regulators are now asking about AI suppliers. Pick your market in the first question and get a personalised scorecard like the sample below, mapped to the rules that reach you. A few minutes.",
    },
    generic: {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "Operational resilience with an AI readiness reading: how fast you detect, escalate and recover - and whether you can prove it. Pick your market in the first question and get a personalised scorecard like the sample below. A few minutes.",
    },
  };
  HOME_PARTIAL_INDUSTRY["digital-lite"] = HOME_PARTIAL_INDUSTRY.digital;

  var INDUSTRY_PREVIEW_AXES = {
    fsi: ["ops", "security", "privacy", "ai"],
    telco: ["svc", "ops", "security", "privacy", "ai"],
    ecom: ["peak", "ops", "security", "privacy", "ai"],
    digital: ["cust", "ops", "security", "privacy", "ai"],
    generic: ["ops", "security", "privacy", "ai"],
  };

  var GEO_INDUSTRY_COPY = {
    au: "Australia's rules are moving fast: new obligations for telcos, privacy penalties with real teeth, operational risk standards for financial services.",
    sg: "Singapore expects fast answers: incident clocks are measured in hours, and new resilience rules are landing for digital infrastructure.",
    nz: "New Zealand's regulators are asking harder questions: cyber resilience self-assessments, incident reporting, 111 continuity.",
    hk: "Hong Kong's rules just tightened: operational-resilience deadlines for banks, 12-hour incident reporting for critical infrastructure under Cap. 653.",
    my: "Malaysia's clocks are short: six hours for critical-sector incident particulars, 72 hours for data breaches, and BNM's RMiT for financial services.",
    th: "Thailand runs real clocks: 24 hours for critical-infrastructure incidents, 72 hours for data breaches, and BOT guidelines for financial services.",
    kr: "Korea's bar is rising: the AI Basic Act is in force, PIPA breaches run on a 72-hour clock, and e-finance incidents have their own regime.",
    other:
      "Different markets, same test: when things break, how fast do you know, respond and recover?",
  };

  var OVERLAYS = {
    telco: [
      {
        id: "T1",
        sheetId: 21,
        domain: "svc",
        tier: "answers",
        text: "A network outage is affecting emergency call delivery. Walk through your first 30 minutes.",
        anchors: [
          "We would learn of emergency-call impact from *external reports*.",
          "The outage would be detected, but assessing emergency-call impact would *take time*.",
          "Emergency-call impact is flagged in monitoring; response steps are documented but *not rehearsed*.",
          "Automated detection flags emergency-call impact, a *rehearsed playbook* {ECS_PLAYBOOK}, and every step is timestamped.",
        ],
      },
      {
        id: "T2",
        sheetId: 22,
        domain: "svc",
        tier: "answers",
        text: "A major outage hits {MAJOR_OUTAGE}. Could you meet your customer-communication obligations while running the response?",
        anchors: [
          "Customer communication would be *improvised* and would compete with the response for the same people.",
          "Templates exist, but publishing *depends on the people running the response*.",
          "Communication roles are *separated from response roles*, though timeliness is not measured against the standard.",
          "Stakeholder updates run on a *rehearsed cadence* alongside the response, with timing evidenced against the standard.",
        ],
      },
      {
        id: "T3",
        sheetId: 23,
        domain: "ops",
        tier: "informs",
        text: dated(
          "2026-10-04",
          "Where are you against your chosen framework's maturity indicator 1, due 3 October 2026?",
          "Maturity indicator 1 fell due on 3 October 2026 - where did you land against your chosen framework?",
        ),
        anchors: [
          "We have *not yet mapped* ourselves against the chosen framework.",
          "A *gap assessment is underway*; remediation is not yet scheduled.",
          dated(
            "2026-10-04",
            "Gaps are known and *remediation is planned* against the October date.",
            "Gaps against MI1 are known and *remediation is underway*.",
          ),
          "MI1 requirements are *met and evidenced*; MI2 planning has started.",
        ],
      },
    ],
    ecom: [
      {
        id: "E1",
        sheetId: 24,
        domain: "peak",
        tier: "answers",
        text: "It's your biggest trading hour of the year and checkout error rates spike. What happens in the first 15 minutes?",
        anchors: [
          "We would likely hear from *customers or the operations floor* before our alerts.",
          "Alerts would fire, but triage and escalation depend on *who is around*.",
          "Peak-period alerting is tuned and an escalation path exists, though it is *not rehearsed at peak load*.",
          "Detection is tuned for peak, escalation is rehearsed, and a *war-room playbook* starts within minutes.",
        ],
      },
      {
        id: "E2",
        sheetId: 25,
        domain: "peak",
        tier: "answers",
        text: "Do you know your revenue cost per minute of checkout downtime - and is there an agreed threshold that triggers escalation?",
        anchors: [
          "Downtime cost has *not been quantified*.",
          "A figure exists in a business case, but it does *not drive operations*.",
          "The cost is known and informs prioritisation, but *nothing escalates automatically*.",
          "Cost per minute is agreed and monitored, and breaching the threshold *escalates automatically*.",
        ],
      },
      {
        id: "E3",
        sheetId: 26,
        domain: "peak",
        tier: "supports",
        text: "A payment provider degrades during peak. How quickly do you know it's them and not you?",
        anchors: [
          "We would spend the first hour *ruling out our own systems*.",
          "Provider status pages are checked *manually* once suspicion arises.",
          "Provider health is monitored, but correlating it with our checkout errors is *manual*.",
          "Provider signals are *correlated with our own telemetry*, so third-party fault is identified in minutes and the vendor playbook starts.",
        ],
      },
    ],
    digital: [
      {
        id: "D1",
        sheetId: 27,
        domain: "cust",
        tier: "answers",
        text: "An enterprise customer's regulator requires them to evidence YOUR incident response. Could you produce it?",
        anchors: [
          "Assembling it would be *a project* across tickets, chat logs and memory.",
          "Records exist in several systems; consolidation would *take days*.",
          "Incident records are structured, though customer-facing extracts need *manual work*.",
          "*Exportable per customer*, with timestamps and named responders, ready without a scramble.",
        ],
      },
      {
        id: "D2",
        sheetId: 28,
        domain: "cust",
        tier: "answers",
        text: "During a multi-tenant incident, how do you run customer-facing communications alongside the response?",
        anchors: [
          "Communications are *drafted from scratch* during the incident.",
          "A status page exists, but updates *lag the response* and customer messaging is improvised.",
          "Status page and templates exist with assigned owners, though proactive notification is *inconsistent*.",
          "Status page, proactive customer notifications and executive updates run on a *rehearsed cadence*, integrated with the response.",
        ],
      },
      {
        id: "D3",
        sheetId: 29,
        domain: "cust",
        tier: "informs",
        text: "Do you have an inventory of the regulatory obligations flowing down through your enterprise contracts?",
        anchors: [
          "We have *not mapped* the obligations inside enterprise contracts.",
          "Known for the *largest accounts only*, and held by the account team.",
          "An inventory exists but is *not maintained* as contracts renew.",
          "A *maintained inventory with owners*, reviewed at renewal, and tied to incident and evidence processes.",
        ],
      },
    ],
  };

  var CORE_ORDER = [
    "S3",
    "S1",
    "S9",
    "S11",
    "S2",
    "S5",
    "S6",
    "S4",
    "S10",
    "S12",
    "S7",
    "S8",
  ];

  CONFIRM_BY_CELL["au-digital-lite"] = {
    sub: "Use your report to identify gaps and plan next steps across the SOCI Act's data-storage and processing obligations, the Notifiable Data Breaches scheme and AI governance.",
    tiles: [
      {
        title: "What AI changes about incident response",
        desc: "AI is already inside claims, payments and fraud workflows. What gets asked when one misbehaves, and what your operating record needs to show.",
        url: "https://utm.io/ai-blog",
        cta: "Read the article",
      },
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: CONFIRM_BY_CELL["au-digital"].contact,
  };
  CONFIRM_BY_CELL["other-fsi"] = {
    sub: "Use your report to identify gaps and plan next steps across operational-resilience regimes such as DORA and the UK's PS21/3, data-protection law and AI governance.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  };
  CONFIRM_BY_CELL["other-telco"] = {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, critical-infrastructure incident reporting, data-protection law and AI governance.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  };
  CONFIRM_BY_CELL["other-ecom"] = {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, data-protection law, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  };
  CONFIRM_BY_CELL["other-digital"] = {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, data-protection law, cyber-incident reporting and AI governance.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  };

  var PDF_META = {
    default: {
      title: "Operational Readiness Assessment",
      filename: "Operational-Readiness-Assessment.pdf",
      linkLabel: "pagerduty.com",
      url: "https://www.pagerduty.com/",
    },

    au: {
      title: "Operational Readiness Assessment",
      filename: "Operational-Readiness-Assessment.pdf",
      linkLabel: "pagerduty.com/apra",
      url: "https://www.pagerduty.com/apra/",
    },
  };

  function pdfMetaFor() {
    return (
      own(PDF_META, appState.cell) ||
      own(PDF_META, appState.geo) ||
      PDF_META["default"]
    );
  }

  var appState = {
    route: null,
    industry: null,
    geo: null,
    cell: null,
    degraded: false,
    phase: null,
    restQs: [],
    runDomainOrder: [],
    domainTier: {},
    params: {},
    cellLocked: false,
    geoSource: "",
    showPicker: false,
  };
  var _domainCache = {};

  var INDUSTRY_ALIASES = {
    fsi: "fsi",
    apra: "fsi",
    banking: "fsi",
    "financial-services": "fsi",
    finance: "fsi",
    "fsi-lite": "fsi-lite",
    fintech: "fsi-lite",
    telco: "telco",
    telecom: "telco",
    telecommunications: "telco",
    ecom: "ecom",
    retail: "ecom",
    ecommerce: "ecom",
    digital: "digital",
    tech: "digital",
    technology: "digital",
    "digital-lite": "digital-lite",
    generic: "generic",
    other: "other",
  };
  var GEO_ALIASES = {
    au: "au",
    aus: "au",
    australia: "au",
    sg: "sg",
    singapore: "sg",
    nz: "nz",
    "new-zealand": "nz",
    newzealand: "nz",
    hk: "hk",
    hongkong: "hk",
    "hong-kong": "hk",
    my: "my",
    malaysia: "my",
    th: "th",
    thailand: "th",
    kr: "kr",
    sk: "kr",
    korea: "kr",
    "south-korea": "kr",
    southkorea: "kr",
    other: "other",
    global: "other",
  };

  function own(map, key) {
    return Object.prototype.hasOwnProperty.call(map, key)
      ? map[key]
      : undefined;
  }

  function normaliseIndustry(v) {
    if (!v) return null;
    v = String(v).toLowerCase();
    return own(INDUSTRY_ALIASES, v) || v;
  }
  function normaliseGeo(v) {
    if (!v) return null;
    v = String(v).toLowerCase();
    return own(GEO_ALIASES, v) || v;
  }

  function resolveCell(industry, geo) {
    appState.industry = industry;
    appState.geo = geo;
    appState.degraded = false;
    _domainCache = {};
    var key = geo ? geo + "-" + industry : "default";
    if (industry === "generic") key = geo ? geo + "-generic" : "default";
    if (!CELLS[key]) {
      key = geo ? geo + "-generic" : "default";
      appState.degraded = true;
    }
    if (!CELLS[key]) {
      appState.geo = "other";
      key = CELLS["other-" + industry] ? "other-" + industry : "other-generic";
      appState.degraded = true;
    }
    if (CELLS[key] && !CELLS[key].built) {
      key = CELLS[key].geo + "-generic";
      appState.degraded = true;
    }
    appState.cell = key;
  }

  function fill(text) {
    var s = SLOTS[appState.cell] || SLOTS["default"];
    var out = text.replace(/\{(\w+)\}/g, function (m, k) {
      if (k === "PEER_NOUN") return peerNoun();
      return s[k] !== null && s[k] !== undefined ? s[k] : m;
    });
    return out.replace(/s's\b/g, "s'");
  }

  function questionTextFor(q) {
    if (q.id === "S4") {
      var s4 = SLOTS[appState.cell] || SLOTS["default"];
      if (/required$/.test(s4.SEC_CLOCK || "")) {
        return "A security incident occurs. How confident are you in meeting your notification obligations on time?";
      }
    }
    if (q.id === "S6") {
      var s = SLOTS[appState.cell] || SLOTS["default"];
      if (s.CYBER_AGENCY)
        return (
          s.CYBER_Q ||
          "Could you report a critical incident to " +
            s.CYBER_AGENCY +
            " within " +
            s.CYBER_CLOCK +
            " - including at 2 AM on a long weekend?"
        );
      return "Could you meet your notification obligations at 2 AM on a long weekend?";
    }
    if (q.id === "S10" && CELLS[appState.cell].generic)
      return "If a regulator, auditor or enterprise customer requested evidence of your last 10 critical incidents, could you provide it?";
    return fill(q.text);
  }

  function materialiseQuestion(q) {
    return {
      id: q.id,
      sheetId: q.sheetId,
      domain: q.domain,
      tier: q.tier,
      question: questionTextFor(q),
      options: q.anchors.map(function (a, i) {
        var marked = fill(a);
        marked = marked.charAt(0).toUpperCase() + marked.slice(1);
        return {
          display: marked,
          text: marked.replace(/\*/g, ""),
          score: i + 1,
        };
      }),
    };
  }

  function optionHTML(option) {
    var body = esc(option.display || option.text).replace(
      /\*([^*]+)\*/g,
      "<strong>$1</strong>",
    );
    return (
      "<strong>" + MATURITY_LABELS[option.score - 1] + "</strong> - " + body
    );
  }

  function buildQuestions() {
    var cell = CELLS[appState.cell];
    var overlayQs = cell.overlay
      ? OVERLAYS[cell.overlay].filter(function (q) {
          return !(
            cell.overlayExclude && cell.overlayExclude.indexOf(q.id) >= 0
          );
        })
      : [];
    var byId = {};
    SPINE.forEach(function (q) {
      byId[q.id] = q;
    });
    var order = CORE_ORDER.filter(function (id) {
      return !(cell.generic && (id === "S4" || id === "S6"));
    });
    var core = overlayQs
      .concat(
        order.map(function (id) {
          return byId[id];
        }),
      )
      .concat([byId["AI1"], byId["AI2"], byId["AI3"]]);
    function remap(q) {
      var c = {};
      for (var k in q) c[k] = q[k];
      if (cell.domainRemap && cell.domainRemap[q.id])
        c.domain = cell.domainRemap[q.id];
      return c;
    }
    var mapped = core.map(remap);
    var axisOrder = [];
    mapped.forEach(function (q) {
      if (axisOrder.indexOf(q.domain) === -1) axisOrder.push(q.domain);
    });
    var grouped = [];
    axisOrder.forEach(function (d) {
      mapped.forEach(function (q) {
        if (q.domain === d) grouped.push(q);
      });
    });
    var hookQs = [];
    axisOrder.forEach(function (d) {
      var block = grouped.filter(function (q) {
        return q.domain === d;
      });
      var pick = null;
      for (var i = 0; i < block.length; i++)
        if (block[i].tier === "answers") {
          pick = block[i];
          break;
        }
      hookQs.push(pick || block[0]);
    });
    var rest = grouped.filter(function (q) {
      return hookQs.indexOf(q) === -1;
    });
    for (var bi = 1; bi < rest.length; bi++) {
      if (rest[bi].domain !== rest[bi - 1].domain) rest[bi]._beat = true;
    }
    return { hook: hookQs, rest: rest };
  }

  function getDomain(slug) {
    if (_domainCache[slug]) return _domainCache[slug];
    var geo = CELLS[appState.cell] ? CELLS[appState.cell].geo : null;
    var merged = {};
    [
      DOMAINS[slug],
      geo && DOMAIN_OVERRIDES[geo] && DOMAIN_OVERRIDES[geo][slug],
      DOMAIN_OVERRIDES[appState.cell] && DOMAIN_OVERRIDES[appState.cell][slug],
    ].forEach(function (layer) {
      if (layer) for (var k in layer) merged[k] = layer[k];
    });
    var out = {};
    for (var k in merged)
      out[k] = typeof merged[k] === "string" ? fill(merged[k]) : merged[k];
    _domainCache[slug] = out;
    return out;
  }

  function apraClass() {
    if (appState.cell === "au-fsi" || appState.cell === "au-digital")
      return "APRA";
    if (
      appState.cell === "au-fsi-lite" ||
      appState.cell === "sg-fsi" ||
      appState.cell === "nz-fsi" ||
      appState.cell === "hk-fsi" ||
      appState.cell === "my-fsi" ||
      appState.cell === "th-fsi" ||
      appState.cell === "kr-fsi" ||
      appState.cell === "other-fsi"
    )
      return "FSI";
    return "NONE";
  }

  function cellProps(extra) {
    var base = {
      assessment: ASSESSMENT_NAME,
      industry: appState.industry || "",
      geo: appState.geo || "",
      cell: appState.cell || "",
      apra: apraClass(),
      degraded: appState.degraded,
      geo_source: appState.geoSource || "",
      ai_focus: appState.focusAI ? "yes" : "",
    };
    if (extra) for (var k in extra) base[k] = extra[k];
    return base;
  }

  function buildResultsLink() {
    var parts = [
      "country=" + encodeURIComponent(appState.geo || "other"),
      "industry=" + encodeURIComponent(appState.industry || "other"),
      "apra=" + (apraClass() === "APRA" ? "yes" : "no"),
    ];
    for (var dk in answers) {
      var da = answers[dk];
      for (var i = 0; i < da.length; i++)
        parts.push("q" + da[i].qid + "=" + da[i].score);
    }
    return window.location.origin + RESULTS_PATH + "?" + parts.join("&");
  }

  function scoreBasisLine() {
    var n = 0;
    for (var k in answers)
      if (answers.hasOwnProperty(k)) n += answers[k].length;
    return "Based on the " + n + " questions you answered.";
  }

  var currentQuestion = 0;
  var answers = {};
  var selectedAnswer = null;
  var shuffledQuestions = [];
  var expandedDomain = null;

  function getTier(pct) {
    for (var i = 0; i < readinessTiers.length; i++) {
      if (pct >= readinessTiers[i].min && pct <= readinessTiers[i].max)
        return readinessTiers[i];
    }
    return readinessTiers[0];
  }

  function getDomainVerdict(pct) {
    if (pct <= 25) return "Not ready";
    if (pct <= 50) return "Gaps to close";
    if (pct <= 75) return "On track";
    return "Strong";
  }

  function getDomainColour(pct) {
    if (pct <= 25) return "#DC2626";
    if (pct <= 50) return "#F59E0B";
    if (pct <= 75) return "#048A24";
    return "#048A24";
  }

  function showScreen(id) {
    var s = document.querySelectorAll(".screen");
    for (var i = 0; i < s.length; i++) s[i].classList.remove("active");
    document.getElementById(id).classList.add("active");
    window.scrollTo(0, 0);
    if (DEBUG && typeof renderDebugSkip === "function") renderDebugSkip();
  }

  function esc(str) {
    var d = document.createElement("div");
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  var GEO_NAMES = {
    au: "Australia",
    sg: "Singapore",
    nz: "New Zealand",
    hk: "Hong Kong",
    my: "Malaysia",
    th: "Thailand",
    kr: "South Korea",
    other: "Global",
  };

  var GEO_PICKER = [
    ["au", "AU", "Australia"],
    ["sg", "SG", "Singapore"],
    ["nz", "NZ", "New Zealand"],
    ["hk", "HK", "Hong Kong"],
    ["my", "MY", "Malaysia"],
    ["th", "TH", "Thailand"],
    ["kr", "KR", "South Korea"],
  ];
  var INDUSTRY_NOUN = {
    fsi: "financial services",
    telco: "telecommunications",
    ecom: "retail & ecommerce",
    digital: "technology & digital",
  };

  var PEER_NOUN_BY_QUALIFIER = {
    "Bank or mutual": "banks",
    Insurer: "insurers",
    "Superannuation fund": "super funds",
    "Fintech, payments or other financial services": "fintechs",
  };
  var PEER_NOUN_BY_INDUSTRY = {
    fsi: "financial services organisations",
    telco: "telcos",
    ecom: "retailers",
    digital: "technology providers",
  };
  function peerNoun() {
    var q = appState.qualifier;
    if (q && PEER_NOUN_BY_QUALIFIER[q]) return PEER_NOUN_BY_QUALIFIER[q];
    var ind = String(appState.industry || "").replace(/-lite$/, "");
    return PEER_NOUN_BY_INDUSTRY[ind] || "organisations";
  }

  function renderHome() {
    var home = HOME[appState.cell] || HOME["default"];
    if (appState.route === "params-partial") {
      if (appState.geo && !appState.industry) {
        home = HOME_PARTIAL_GEO[appState.geo] || home;
      } else if (appState.industry && !appState.geo) {
        home =
          HOME_PARTIAL_INDUSTRY[appState.industry] ||
          HOME_PARTIAL_INDUSTRY[
            String(appState.industry).replace("-lite", "")
          ] ||
          home;
      }
    }
    if (
      appState.geo === "au" &&
      appState.industry === "fsi" &&
      !appState.cellLocked
    ) {
      home = HOME_AU_FSI_OPEN;
    }
    if (appState.focusAI) {
      home = AI_FOCUS_HOME[appState.geo] || AI_FOCUS_HOME["default"];
    }
    document.getElementById("home-headline").textContent = home.h1;
    document.getElementById("home-sub").textContent = home.sub;
    var fullyResolved = !!(appState.geo && appState.industry);
    document.getElementById("start-cta").textContent = fullyResolved
      ? "Start the assessment"
      : "Start the check";
    document.getElementById("home-startover").style.display =
      fullyResolved && appState.route === "params" && !regionLineMode()
        ? "block"
        : "none";
    var startoverLink = document.getElementById("home-startover-link");
    if (startoverLink) {
      startoverLink.textContent = appState.showPicker
        ? "Wrong market or industry? Change it here"
        : "Wrong industry? Change it here";
    }
    updateRegionLine();
    startSampleWidget();
    renderDebugPanel();
    showScreen("screen-start");
  }

  var SAMPLE_PROFILES = [
    [91, 78, 75, 50, 83],
    [88, 75, 38, 75, 60],
    [55, 90, 62, 71, 45],
  ];
  var _sampleTimer = null;
  var _sample = null; // { elId, axes, cur, geom, refs }

  function sampleDomain(slug) {
    if (
      appState.geo === "au" &&
      appState.industry === "fsi" &&
      !appState.cellLocked
    ) {
      var merged = {};
      [
        DOMAINS[slug],
        DOMAIN_OVERRIDES["au"] && DOMAIN_OVERRIDES["au"][slug],
      ].forEach(function (src) {
        if (src) {
          for (var k in src) merged[k] = src[k];
        }
      });
      return merged;
    }
    return getDomain(slug);
  }

  function homeAxes() {
    if (
      appState.route === "params-partial" &&
      appState.industry &&
      !appState.geo
    ) {
      var preview =
        INDUSTRY_PREVIEW_AXES[appState.industry] ||
        INDUSTRY_PREVIEW_AXES[String(appState.industry).replace("-lite", "")];
      if (preview) return preview;
    }
    if (
      appState.geo === "au" &&
      appState.industry === "fsi" &&
      !appState.cellLocked
    ) {
      return INDUSTRY_PREVIEW_AXES.fsi;
    }
    var built = buildQuestions();
    var core = built.hook.concat(built.rest);
    return DOMAIN_CANON.filter(function (slug) {
      return core.some(function (q) {
        return q.domain === slug;
      });
    });
  }

  function _profileScores(axes, idx) {
    var vals = SAMPLE_PROFILES[idx % SAMPLE_PROFILES.length];
    var scores = {};
    for (var i = 0; i < axes.length; i++)
      scores[axes[i]] = vals[i % vals.length];
    return scores;
  }

  function _sampleGeom(n) {
    var size = 230,
      padding = 55,
      full = size + padding * 2;
    return {
      full: full,
      cx: full / 2,
      cy: full / 2,
      r: size * 0.44,
      step: (2 * Math.PI) / n,
      start: -Math.PI / 2,
    };
  }

  function _radarPt(g, i, v) {
    var a = g.start + i * g.step,
      d = (v / 100) * g.r;
    return { x: g.cx + d * Math.cos(a), y: g.cy + d * Math.sin(a) };
  }

  function _buildSampleSkeleton(elId, axes, scores) {
    var g = _sampleGeom(axes.length);
    var lm = axes.length >= 5 ? 1.22 : 1.28;
    var ff =
      'font-family="Plain,-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif"';
    var svg =
      '<svg viewBox="0 0 ' +
      g.full +
      " " +
      g.full +
      '" width="100%" style="max-width:' +
      g.full +
      'px;overflow:visible">';
    var levels = [25, 50, 75, 100];
    for (var l = 0; l < levels.length; l++) {
      var pts = [];
      for (var i = 0; i < axes.length; i++) {
        var p = _radarPt(g, i, levels[l]);
        pts.push(p.x + "," + p.y);
      }
      svg +=
        '<polygon points="' +
        pts.join(" ") +
        '" fill="none" stroke="#E5E7EB" stroke-width="' +
        (l === levels.length - 1 ? "1" : "0.5") +
        '" opacity="0.8"/>';
    }
    for (var i = 0; i < axes.length; i++) {
      var p = _radarPt(g, i, 100);
      svg +=
        '<line x1="' +
        g.cx +
        '" y1="' +
        g.cy +
        '" x2="' +
        p.x +
        '" y2="' +
        p.y +
        '" stroke="#E5E7EB" stroke-width="0.5"/>';
    }
    var dpts = axes.map(function (k, i) {
      return _radarPt(g, i, scores[k]);
    });
    svg +=
      '<polygon class="sample-data" points="' +
      dpts
        .map(function (p) {
          return p.x + "," + p.y;
        })
        .join(" ") +
      '" fill="rgba(6,172,56,0.15)" stroke="#048A24" stroke-width="2"/>';
    for (var i = 0; i < axes.length; i++) {
      svg +=
        '<circle class="sample-dot" cx="' +
        dpts[i].x +
        '" cy="' +
        dpts[i].y +
        '" r="4" fill="' +
        getDomainColour(scores[axes[i]]) +
        '" stroke="#FFF" stroke-width="2"/>';
    }
    for (var i = 0; i < axes.length; i++) {
      var a = g.start + i * g.step;
      var lx = g.cx + g.r * lm * Math.cos(a),
        ly = g.cy + g.r * lm * Math.sin(a);
      var cosA = Math.cos(a);
      var anc = Math.abs(cosA) < 0.1 ? "middle" : cosA > 0 ? "start" : "end";
      var d = sampleDomain(axes[i]);
      var lines = d.radarLines || d.name.split(" ");
      svg +=
        '<text x="' +
        lx +
        '" y="' +
        (ly - 6) +
        '" text-anchor="' +
        anc +
        '" fill="#191919" font-size="13" font-weight="700" ' +
        ff +
        ">" +
        esc(lines[0]) +
        "</text>";
      if (lines.length > 1)
        svg +=
          '<text x="' +
          lx +
          '" y="' +
          (ly + 10) +
          '" text-anchor="' +
          anc +
          '" fill="#191919" font-size="13" font-weight="700" ' +
          ff +
          ">" +
          esc(lines.slice(1).join(" ")) +
          "</text>";
    }
    svg += "</svg>";

    var h = "";
    h +=
      '<div class="sample-top"><div><div class="score-label">Overall readiness</div>';
    h += '<span class="score-big sample-overall"></span></div>';
    h += '<span class="tier-badge sample-tier"></span></div>';
    h +=
      '<div class="score-bar"><div class="score-bar-fill sample-topbar"></div></div>';
    h +=
      '<div class="radar-label" style="margin-top:12px">Readiness by domain</div>';
    h += '<div class="sample-cols">';
    h += '<div class="sample-radar">' + svg + "</div>";
    h += '<div class="sample-detail"><div class="sample-detail-body">';
    h +=
      '<div class="sample-detail-toprow"><div><span class="domain-name sample-feat-name"></span> <span class="domain-sub sample-feat-sub"></span></div>';
    h += '<span class="domain-pct sample-feat-pct"></span></div>';
    h +=
      '<div class="domain-bar" style="margin-bottom:10px"><div class="domain-bar-fill sample-feat-bar"></div></div>';
    h +=
      '<div class="detail-label detail-label-red">Why it matters</div><div class="detail-text sample-blur sample-feat-reg" style="margin-bottom:8px"></div>';
    h +=
      '<div class="detail-label detail-label-dark">What good looks like</div><div class="detail-text sample-blur sample-feat-good"></div>';
    h += "</div></div></div>";
    h += '<div class="sample-bars">';
    for (var b = 0; b < axes.length; b++) {
      h +=
        '<div class="sample-bar-box" data-slug="' +
        axes[b] +
        '"><div class="sample-bar-name">' +
        esc(sampleDomain(axes[b]).name) +
        "</div>";
      h +=
        '<div class="sample-bar-row"><div class="domain-bar"><div class="domain-bar-fill"></div></div>';
      h +=
        '<span class="domain-pct" style="font-size:12px;min-width:32px"></span></div></div>';
    }
    h += "</div>";
    h +=
      '<p class="sample-caption">Sample - cycles through different profiles</p>';

    var root = document.getElementById(elId);
    root.innerHTML = h;
    return {
      root: root,
      poly: root.querySelector(".sample-data"),
      dots: root.querySelectorAll(".sample-dot"),
      overall: root.querySelector(".sample-overall"),
      tier: root.querySelector(".sample-tier"),
      topbar: root.querySelector(".sample-topbar"),
      detail: root.querySelector(".sample-detail"),
      detailBody: root.querySelector(".sample-detail-body"),
      featName: root.querySelector(".sample-feat-name"),
      featSub: root.querySelector(".sample-feat-sub"),
      featPct: root.querySelector(".sample-feat-pct"),
      featBar: root.querySelector(".sample-feat-bar"),
      featReg: root.querySelector(".sample-feat-reg"),
      featGood: root.querySelector(".sample-feat-good"),
      boxes: root.querySelectorAll(".sample-bar-box"),
    };
  }

  function _applySampleProfile(scores, animateFrom) {
    var s = _sample,
      g = s.geom,
      axes = s.axes,
      refs = s.refs;
    var total = 0;
    axes.forEach(function (k) {
      total += scores[k];
    });
    var overall = Math.round(total / axes.length);
    var tier = getTier(overall);
    var weakest = axes.slice().sort(function (a, b) {
      return scores[a] - scores[b];
    })[0];
    var wcol = getDomainColour(scores[weakest]);
    var wd = sampleDomain(weakest);

    refs.overall.textContent = overall + "%";
    refs.overall.style.color = tier.colour;
    refs.tier.textContent = tier.label;
    refs.tier.style.background = tier.bg;
    refs.tier.style.color = tier.colour;
    refs.topbar.style.width = overall + "%";
    refs.topbar.style.background = tier.colour;

    refs.boxes.forEach(function (box) {
      var k = box.getAttribute("data-slug");
      var col = getDomainColour(scores[k]);
      var fill = box.querySelector(".domain-bar-fill");
      fill.style.width = scores[k] + "%";
      fill.style.background = col;
      var pct = box.querySelector(".domain-pct");
      pct.textContent = scores[k] + "%";
      pct.style.color = col;
      box.classList.toggle("live", k === weakest);
    });

    var setFeat = function () {
      refs.featName.textContent = wd.name;
      refs.featSub.textContent = wd.subtitle;
      refs.featPct.textContent = scores[weakest] + "%";
      refs.featPct.style.color = wcol;
      refs.featBar.style.width = scores[weakest] + "%";
      refs.featBar.style.background = wcol;
      refs.featReg.textContent = wd.regRisk;
      refs.featGood.textContent = wd.whatGoodLooksLike;
      refs.detail.style.borderLeftColor = wcol;
    };
    if (animateFrom && s.featSlug !== weakest) {
      refs.detailBody.style.opacity = "0";
      setTimeout(function () {
        setFeat();
        refs.detailBody.style.opacity = "1";
      }, 300);
    } else {
      setFeat();
    }
    s.featSlug = weakest;

    if (animateFrom) {
      var from = animateFrom,
        t0 = null,
        DUR = 700;
      var frame = function (ts) {
        if (s.cancelled) return;
        if (!t0) t0 = ts;
        var t = Math.min(1, (ts - t0) / DUR);
        var e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // easeInOutQuad
        var pts = [];
        for (var i = 0; i < axes.length; i++) {
          var v = from[axes[i]] + (scores[axes[i]] - from[axes[i]]) * e;
          var p = _radarPt(g, i, v);
          pts.push(p);
        }
        refs.poly.setAttribute(
          "points",
          pts
            .map(function (p) {
              return p.x + "," + p.y;
            })
            .join(" "),
        );
        for (var i = 0; i < axes.length; i++) {
          refs.dots[i].setAttribute("cx", pts[i].x);
          refs.dots[i].setAttribute("cy", pts[i].y);
        }
        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          for (var i = 0; i < axes.length; i++)
            refs.dots[i].setAttribute("fill", getDomainColour(scores[axes[i]]));
        }
      };
      requestAnimationFrame(frame);
    }
    s.cur = scores;
  }

  function startSampleWidget(elId) {
    elId = elId || "sample-inner";
    stopSampleWidget();
    var axes = homeAxes();
    var scores0 = _profileScores(axes, 0);
    _sample = {
      elId: elId,
      axes: axes,
      geom: _sampleGeom(axes.length),
      cur: scores0,
      featSlug: null,
      idx: 0,
      cancelled: false,
    };
    _sample.refs = _buildSampleSkeleton(elId, axes, scores0);
    _applySampleProfile(scores0, null);
    _sampleTimer = setInterval(function () {
      _sample.idx++;
      _applySampleProfile(
        _profileScores(_sample.axes, _sample.idx),
        _sample.cur,
      );
    }, 4500);
  }

  function stopSampleWidget() {
    if (_sampleTimer) {
      clearInterval(_sampleTimer);
      _sampleTimer = null;
    }
    if (_sample) _sample.cancelled = true;
  }

  function _setupOption(onclick, title, small, cls) {
    return (
      '<button class="option-btn' +
      (cls ? " " + cls : "") +
      '" onclick="' +
      onclick +
      '"><strong>' +
      title +
      "</strong>" +
      (small ? "<small>" + small + "</small>" : "") +
      "</button>"
    );
  }

  function _setupRegionNote() {
    return regionLineMode()
      ? '<p class="privacy-note" style="margin:14px 0 0;color:#9CA3AF">' +
          regionLineHTML("setup") +
          "</p>"
      : "";
  }

  function nextSetupStep() {
    var optionsEl = document.getElementById("setup-options");
    optionsEl.className = "picker-list";
    if (!appState.geo && !appState.showPicker) {
      appState.geo = DEFAULT_GEO;
      appState.geoSource = "default";
    }
    if (!appState.geo) {
      document.getElementById("setup-question").textContent =
        "First - which market's rules apply to you?";
      var note = document.getElementById("setup-note");
      note.style.display = "block";
      note.textContent =
        "Pick where your customers and regulators are - even if headquarters is somewhere else. That's what we map your results against.";
      optionsEl.className = "picker-list picker-grid";
      var globeBadge =
        '<span class="geo-badge"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z"/></svg></span>';
      var opts = GEO_PICKER.map(function (g) {
        return {
          onclick: "chooseGeo('" + g[0] + "')",
          label: '<span class="geo-badge">' + g[1] + "</span>" + g[2],
        };
      });
      opts.push({
        onclick: "chooseGeo('other')",
        label: globeBadge + "Somewhere else",
      });
      optionsEl.innerHTML = opts
        .map(function (o, i) {
          return _setupOption(
            o.onclick,
            o.label,
            "",
            opts.length % 2 === 1 && i === opts.length - 1 ? "picker-span" : "",
          );
        })
        .join("");
      showScreen("screen-setup");
      return;
    }
    if (!appState.industry) {
      document.getElementById("setup-question").textContent =
        "Which of these best describes your organisation?";
      var note = document.getElementById("setup-note");
      note.style.display = "block";
      note.textContent =
        GEO_INDUSTRY_COPY[appState.geo] || GEO_INDUSTRY_COPY.other;
      document.getElementById("setup-options").innerHTML =
        _setupOption(
          "chooseIndustry('fsi')",
          "Financial services",
          esc(FSI_DESCRIPTOR[appState.geo] || FSI_DESCRIPTOR.other),
        ) +
        _setupOption(
          "chooseIndustry('telco')",
          "Telecommunications",
          "carriers &amp; service providers",
        ) +
        _setupOption(
          "chooseIndustry('ecom')",
          "Retail &amp; ecommerce",
          "online &amp; omnichannel trade",
        ) +
        _setupOption(
          "chooseIndustry('digital')",
          "Technology &amp; digital",
          "SaaS, platforms, data centres &amp; cloud",
        ) +
        _setupOption("chooseIndustry('other')", "Something else", "") +
        _setupRegionNote();
      showScreen("screen-setup");
      return;
    }
    if (
      appState.geo === "au" &&
      appState.industry === "fsi" &&
      !appState.cellLocked
    ) {
      document.getElementById("setup-question").textContent =
        "Financial services covers a lot of ground - which best describes you?";
      document.getElementById("setup-note").style.display = "none";
      document.getElementById("setup-options").innerHTML =
        _setupOption(
          "chooseCell('au-fsi', 'Bank or mutual')",
          "Bank or mutual",
          "",
        ) +
        _setupOption("chooseCell('au-fsi', 'Insurer')", "Insurer", "") +
        _setupOption(
          "chooseCell('au-fsi', 'Superannuation fund')",
          "Superannuation fund",
          "",
        ) +
        _setupOption(
          "chooseCell('au-fsi-lite', 'Fintech, payments or other financial services')",
          "Fintech, payments or other financial services",
          "",
        ) +
        _setupRegionNote();
      showScreen("screen-setup");
      return;
    }
    if (
      appState.geo === "au" &&
      appState.industry === "digital" &&
      !appState.cellLocked
    ) {
      document.getElementById("setup-question").textContent =
        "Do you provide services to APRA-regulated organisations?";
      document.getElementById("setup-note").style.display = "none";
      document.getElementById("setup-options").innerHTML =
        _setupOption(
          "chooseCell('au-digital', 'Serves APRA-regulated customers')",
          "Yes - we serve APRA-regulated customers",
          "for example banks, insurers, super funds or payment providers - their CPS 230 obligations flow into your contracts",
        ) +
        _setupOption(
          "chooseCell('au-digital-lite', 'No APRA-regulated customers')",
          "No",
          "we'll keep it to the obligations that apply to you directly",
        ) +
        _setupRegionNote();
      showScreen("screen-setup");
      return;
    }
    if (!appState.cellLocked) {
      var resolved =
        appState.industry === "other" ? "generic" : appState.industry;
      resolveCell(resolved, appState.geo);
      if (appState.industry === "other") appState.industry = "other";
    }
    renderDebugPanel();
    beginScenarios();
  }

  function chooseGeo(geo) {
    appState.geo = geo;
    appState.geoSource = "picker";
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_setup_selected",
        field: "geo",
        value: geo,
      }),
    );
    renderDebugPanel();
    nextSetupStep();
  }

  function chooseIndustry(ind) {
    appState.industry = ind;
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_setup_selected",
        field: "industry",
        value: ind,
      }),
    );
    renderDebugPanel();
    nextSetupStep();
  }

  function chooseCell(cell, label) {
    appState.cell = cell;
    appState.cellLocked = true;
    appState.degraded = false;
    appState.qualifier = label || "";
    _domainCache = {};
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_setup_selected",
        field: "qualifier",
        value: label || cell,
      }),
    );
    renderDebugPanel();
    nextSetupStep();
  }

  function resetSelections() {
    segTrack(
      "Generic Event",
      cellProps({ event_type: "assessment_setup_reopened" }),
    );
    stopSampleWidget();
    clearRegionSelection();
    appState.industry = null;
    appState.route = appState.showPicker ? "cold" : "params-partial";
    appState.cell =
      !appState.showPicker && appState.geo && CELLS[appState.geo + "-generic"]
        ? appState.geo + "-generic"
        : "default";
    nextSetupStep();
  }

  function needsSetupSteps() {
    return (
      !appState.geo ||
      !appState.industry ||
      (appState.geo === "au" &&
        appState.industry === "fsi" &&
        !appState.cellLocked) ||
      (appState.geo === "au" &&
        appState.industry === "digital" &&
        !appState.cellLocked)
    );
  }

  function startAssessment() {
    stopSampleWidget();
    if (needsSetupSteps()) {
      nextSetupStep();
      return;
    }
    beginScenarios();
  }

  function beginScenarios() {
    currentQuestion = 0;
    answers = {};
    appState.startedAt = Date.now();
    selectedAnswer = null;
    expandedDomain = null;
    appState.beatSeen = false;
    var built = buildQuestions();
    appState.phase = "hook";
    appState.restQs = built.rest;
    appState.hookTotal = built.hook.length;
    appState.runId =
      Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
    var allCore = built.hook.concat(built.rest);
    appState.runDomainOrder = DOMAIN_CANON.filter(function (slug) {
      return allCore.some(function (q) {
        return q.domain === slug;
      });
    });
    appState.domainTier = {};
    var tierRank = { informs: 0, supports: 1, answers: 2 };
    allCore.forEach(function (q) {
      var cur = appState.domainTier[q.domain];
      if (!cur || tierRank[q.tier] > tierRank[cur])
        appState.domainTier[q.domain] = q.tier;
    });
    shuffledQuestions = built.hook.map(function (q) {
      return materialiseQuestion(q);
    });
    showScreen("screen-question");
    renderQuestion();
  }

  var BEAT_FACTS = {
    ops: "Keep the map of critical operations current, so when something breaks you already know what it touches.",
    security:
      "Detection, escalation and evidence capture should be able to run in parallel.",
    privacy:
      "A breach response should let security, legal and communications all begin work straight away.",
    ai: "AI services still need clear ownership, monitoring and a tested fallback.",
    svc: "The first hour of a major outage is worth rehearsing long before one happens.",
    peak: "Knowing your cost per minute of downtime is what makes an escalation threshold worth setting.",
    cust: "Enterprise customers increasingly ask for incident evidence they can hand to their own auditors.",
    cps234:
      "Detection, escalation and evidence capture should be able to run in parallel.",
    soci: "Out-of-hours escalation is worth rehearsing, and reporting templates are worth writing before anyone needs them.",
  };
  var _beatTimer = null;
  function buildBeatRadar(pulseAxis, settleAxis) {
    var ds = getDomainScores();
    var axes = appState.runDomainOrder;
    var totals = {};
    shuffledQuestions.forEach(function (q) {
      totals[q.domain] = (totals[q.domain] || 0) + 1;
    });
    var g = _sampleGeom(axes.length);
    var lm = axes.length >= 5 ? 1.22 : 1.28;
    var svg =
      '<svg viewBox="0 0 ' +
      g.full +
      " " +
      g.full +
      '" width="100%" style="max-width:300px;overflow:visible">';
    var levels = [25, 50, 75, 100];
    for (var l = 0; l < levels.length; l++) {
      var pts = [];
      for (var i = 0; i < axes.length; i++) {
        var p = _radarPt(g, i, levels[l]);
        pts.push(p.x + "," + p.y);
      }
      svg +=
        '<polygon points="' +
        pts.join(" ") +
        '" fill="none" stroke="#E5E7EB" stroke-width="' +
        (l === levels.length - 1 ? "1" : "0.5") +
        '" opacity="0.8"/>';
    }
    for (var i = 0; i < axes.length; i++) {
      var p = _radarPt(g, i, 100);
      svg +=
        '<line x1="' +
        g.cx +
        '" y1="' +
        g.cy +
        '" x2="' +
        p.x +
        '" y2="' +
        p.y +
        '" stroke="#E5E7EB" stroke-width="0.5"/>';
    }
    var SETTLE_DUR = "1.1s";
    var BLINK_START = settleAxis ? "1.3s" : "0s"; // next-axis blink waits for the dot to land
    var settleIdx = axes.indexOf(settleAxis);
    var prevScore = null;
    if (settleIdx >= 0 && (answers[settleAxis] || []).length) {
      prevScore = Math.round((answers[settleAxis][0].score / 4) * 100);
    }
    var newPts = [],
      oldPts = [];
    for (var i = 0; i < axes.length; i++) {
      var np = _radarPt(g, i, ds[axes[i]]);
      newPts.push(np.x + "," + np.y);
      var op =
        i === settleIdx && prevScore !== null ? _radarPt(g, i, prevScore) : np;
      oldPts.push(op.x + "," + op.y);
    }
    svg +=
      '<polygon points="' +
      newPts.join(" ") +
      '" fill="#048A24" fill-opacity="0.07" stroke="#048A24" stroke-width="1.5" stroke-opacity="0.55">' +
      (prevScore !== null
        ? '<animate attributeName="points" values="' +
          oldPts.join(" ") +
          ";" +
          newPts.join(" ") +
          '" dur="' +
          SETTLE_DUR +
          '" fill="freeze"/>'
        : "") +
      "</polygon>";
    for (var i = 0; i < axes.length; i++) {
      var k = axes[i];
      var complete = (answers[k] || []).length >= (totals[k] || Infinity);
      if (complete) {
        var dp = _radarPt(g, i, ds[k]);
        var col = getDomainColour(ds[k]);
        if (k === settleAxis && prevScore !== null) {
          var fromP = _radarPt(g, i, prevScore);
          var oldCol = getDomainColour(prevScore);
          var fillAnim =
            oldCol !== col
              ? '<animate attributeName="fill" calcMode="discrete" values="' +
                oldCol +
                ";" +
                col +
                '" keyTimes="0;0.5" dur="' +
                SETTLE_DUR +
                '" fill="freeze"/>'
              : "";
          svg +=
            '<circle r="5" cx="' +
            dp.x +
            '" cy="' +
            dp.y +
            '" fill="' +
            col +
            '" stroke="#FFF" stroke-width="2">' +
            '<animate attributeName="cx" values="' +
            fromP.x +
            ";" +
            dp.x +
            '" dur="' +
            SETTLE_DUR +
            '" fill="freeze"/>' +
            '<animate attributeName="cy" values="' +
            fromP.y +
            ";" +
            dp.y +
            '" dur="' +
            SETTLE_DUR +
            '" fill="freeze"/>' +
            fillAnim +
            "</circle>";
        } else {
          svg +=
            '<circle cx="' +
            dp.x +
            '" cy="' +
            dp.y +
            '" r="5" fill="' +
            col +
            '" stroke="#FFF" stroke-width="2"/>';
        }
      } else if (k === pulseAxis) {
        var bp = _radarPt(g, i, ds[k]);
        svg +=
          '<circle cx="' +
          bp.x +
          '" cy="' +
          bp.y +
          '" r="6" fill="#FFFFFF" stroke="#048A24" stroke-width="2" opacity="0">' +
          '<animate attributeName="opacity" values="0;1" begin="' +
          BLINK_START +
          '" dur="0.2s" fill="freeze"/>' +
          '<animate attributeName="opacity" values="1;0.15;1" begin="' +
          BLINK_START +
          '+0.3s" dur="1.1s" repeatCount="indefinite"/>' +
          "</circle>";
      } else {
        var pp = _radarPt(g, i, ds[k]);
        svg +=
          '<circle cx="' +
          pp.x +
          '" cy="' +
          pp.y +
          '" r="3" fill="#9CA3AF" opacity="0.55"/>';
      }
      var a = g.start + i * g.step;
      var lx = g.cx + g.r * lm * Math.cos(a),
        ly = g.cy + g.r * lm * Math.sin(a);
      var cosA = Math.cos(a);
      var anc = Math.abs(cosA) < 0.1 ? "middle" : cosA > 0 ? "start" : "end";
      var d = getDomain(k);
      var lines = d.radarLines || d.name.split(" ");
      for (var li = 0; li < lines.length; li++) {
        svg +=
          '<text x="' +
          lx +
          '" y="' +
          (ly + li * 11 - (lines.length - 1) * 5) +
          '" text-anchor="' +
          anc +
          '" font-size="10" font-weight="' +
          (complete ? "700" : "400") +
          '" fill="' +
          (complete ? "#191919" : "#9CA3AF") +
          '">' +
          esc(lines[li]) +
          "</text>";
      }
    }
    svg += "</svg>";
    var doneCount = axes.filter(function (k) {
      return (answers[k] || []).length >= (totals[k] || Infinity);
    }).length;
    return { svg: svg, done: doneCount, total: axes.length };
  }
  var _beatPhaseTimer = null;
  function showBeat(nextQ) {
    var prevDomain = shuffledQuestions[currentQuestion - 1].domain;
    var radar = buildBeatRadar(nextQ.domain, prevDomain);
    var prevName = getDomain(prevDomain).name;
    var prevPct = getDomainScores()[prevDomain];
    var ack;
    if (prevPct >= 75)
      ack = "Strong answers on " + prevName + " - " + prevPct + "%.";
    else if (prevPct >= 50)
      ack = prevName + " refined - " + prevPct + "%. A solid base to build on.";
    else
      ack =
        prevName +
        " refined - " +
        prevPct +
        "%. Your scorecard will show where to focus.";
    document.getElementById("beat-ack").textContent = ack;
    document.getElementById("beat-progressline").textContent =
      radar.done + " of " + radar.total + " domains refined";
    document.getElementById("beat-radar").innerHTML = radar.svg;
    document.getElementById("beat-next").textContent = "";
    document.getElementById("beat-fact").textContent = "";
    showScreen("screen-beat");
    appState.beatSeen = true;
    clearTimeout(_beatPhaseTimer);
    _beatPhaseTimer = setTimeout(function () {
      var beat = document.getElementById("screen-beat");
      if (!beat.classList.contains("active")) return;
      var ackEl = document.getElementById("beat-ack");
      var factEl = document.getElementById("beat-fact");
      ackEl.style.opacity = "0";
      setTimeout(function () {
        ackEl.textContent = "Next up: " + getDomain(nextQ.domain).name;
        factEl.textContent = fill(BEAT_FACTS[nextQ.domain] || BEAT_FACTS.ops);
        ackEl.style.opacity = "1";
      }, 180);
    }, 1300);
    clearTimeout(_beatTimer);
    _beatTimer = setTimeout(proceedFromBeat, 4600);
  }
  function proceedFromBeat() {
    clearTimeout(_beatTimer);
    clearTimeout(_beatPhaseTimer);
    var beat = document.getElementById("screen-beat");
    if (!beat.classList.contains("active")) return;
    var ackEl = document.getElementById("beat-ack");
    if (ackEl) ackEl.style.opacity = "1";
    showScreen("screen-question");
    renderQuestion();
  }

  function showSnapshot() {
    showScreen("screen-snapshot");
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);
    var sorted = appState.runDomainOrder.slice().sort(function (a, b) {
      return ds[a] - ds[b];
    });
    var weakest = sorted[0],
      wd = getDomain(weakest);
    var country = GEO_COUNTRY[appState.geo];
    var noun = INDUSTRY_NOUN[appState.industry];
    var tuned;
    if (noun && country)
      tuned =
        "More scenario questions written for " +
        noun +
        " in " +
        country +
        ", using the rules and deadlines that apply to you. The full report gives you:";
    else if (country)
      tuned =
        "More scenario questions based on the rules that apply in " +
        country +
        ". The full report gives you:";
    else
      tuned =
        "More scenario questions based on your regulators and obligations. The full report gives you:";
    var h = "";

    h += '<div class="snap-heading">Your readiness snapshot</div>';
    h +=
      '<div class="snap-sub">Based on ' +
      appState.hookTotal +
      " questions - one from each of your " +
      appState.runDomainOrder.length +
      " domains.</div>";

    h +=
      '<div class="card"><div class="score-header"><div><div class="score-label">Overall readiness pulse</div>';
    h +=
      '<span class="score-big" style="color:' +
      tier.colour +
      '">' +
      overall +
      "%</span></div>";
    h +=
      '<span class="tier-badge" style="background:' +
      tier.bg +
      ";color:" +
      tier.colour +
      '">' +
      esc(tier.label) +
      "</span></div>";
    h +=
      '<div class="score-bar"><div class="score-bar-fill" style="width:' +
      overall +
      "%;background:" +
      tier.colour +
      '"></div></div>';
    h +=
      '<div class="snap-basis">This is a first read - each domain is scored from one answer so far. The full check re-scores everything from the complete question set for your sector.</div></div>';

    h += '<div class="snap-columns">';
    h +=
      '<div class="card"><div class="radar-label">Where you stand so far</div><div class="radar-wrap">' +
      buildRadarSVG(ds) +
      "</div></div>";
    h +=
      '<div class="card"><div class="score-label">Where you look most exposed</div>';
    h +=
      '<div style="font-size:18px;font-weight:700;color:#191919;margin:6px 0 2px">' +
      esc(wd.name) +
      ' <span style="font-size:15px;font-weight:700;color:' +
      getDomainColour(ds[weakest]) +
      '">' +
      ds[weakest] +
      "%</span></div>";
    h +=
      '<div class="detail-section" style="margin-top:10px"><div class="detail-label detail-label-red">Why it matters</div><div class="detail-text">' +
      esc(wd.regRisk) +
      "</div></div>";
    h +=
      '<div class="detail-section"><div class="detail-label detail-label-dark">What good looks like</div><div class="detail-text snap-blur">' +
      esc(wd.whatGoodLooksLike) +
      "</div>";
    h += '<div class="snap-basis">Included in your full report.</div></div>';
    h += "</div></div>";

    var lock =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></svg>';
    h +=
      '<div class="card" style="text-align:center"><div class="cta-title" style="color:#191919">Your full report goes deeper</div>';
    h += '<div class="cta-desc" style="color:#4A4A4A">' + esc(tuned) + "</div>";
    h += '<div style="max-width:440px;margin:0 auto 18px">';
    h +=
      '<div class="snap-locked-row">' +
      lock +
      "Proper scores for every domain, based on the full question set</div>";
    h +=
      '<div class="snap-locked-row">' +
      lock +
      '"What good looks like" and priority fixes for each domain</div>';
    h +=
      '<div class="snap-locked-row">' +
      lock +
      "Your priority areas, ranked - so you know what to fix first</div>";
    h +=
      '<div class="snap-locked-row">' +
      lock +
      "An optional personalised PDF report to review or share internally</div>";
    h += "</div>";
    h +=
      '<button class="btn-primary" onclick="continueToFullQuiz()">Get my full report</button>';
    h += "</div>";

    document.getElementById("snapshot-container").innerHTML = h;
  }

  function continueToFullQuiz() {
    appState.phase = "core";
    appState.beatSeen = true; // the snapshot already showed the shape - full-phase labels can count "N of total"
    var startIdx = shuffledQuestions.length;
    shuffledQuestions = shuffledQuestions.concat(
      appState.restQs.map(function (q) {
        var m = materialiseQuestion(q);
        m.beatBefore = !!q._beat;
        return m;
      }),
    );
    currentQuestion = startIdx;
    selectedAnswer = null;
    showScreen("screen-question");
    renderQuestion();
  }

  function renderQuestion() {
    var q = shuffledQuestions[currentQuestion];
    var total = shuffledQuestions.length;
    var progress = ((currentQuestion + 1) / total) * 100;
    var label;

    if (appState.phase === "hook") {
      label = "Question " + (currentQuestion + 1) + " of " + appState.hookTotal;
      progress = ((currentQuestion + 1) / appState.hookTotal) * 100;
    } else if (!appState.beatSeen) {
      label = "Question " + (currentQuestion + 1);
    } else {
      label = currentQuestion + 1 + " of " + total;
      var thirdMark = Math.floor(total / 3);
      if (currentQuestion >= thirdMark && currentQuestion < total - 3) {
        var minsLeft = Math.ceil((total - currentQuestion) * 0.23);
        label +=
          '<span class="progress-nudge"> - about ' +
          minsLeft +
          " min left</span>";
      } else if (currentQuestion >= total - 3) {
        label += '<span class="progress-nudge-green"> - nearly there</span>';
      }
    }

    document.getElementById("progress-label").innerHTML = label;
    document.getElementById("domain-label").textContent = getDomain(
      q.domain,
    ).name;
    document.getElementById("progress-fill").style.width = progress + "%";
    document.getElementById("question-text").textContent = q.question;

    var c = document.getElementById("options-container");
    c.innerHTML = "";
    for (var i = 0; i < q.options.length; i++) {
      (function (index, option) {
        var btn = document.createElement("button");
        btn.className = "option-btn";
        btn.innerHTML = optionHTML(option);
        btn.addEventListener("click", function () {
          handleAnswer(option, index);
        });
        c.appendChild(btn);
      })(i, q.options[i]);
    }
    selectedAnswer = null;
    updateQuestionRegionLine();
  }

  var _advanceTimer = null,
    _loadingTimer = null;
  function clearAdvanceTimers() {
    if (_advanceTimer) {
      clearTimeout(_advanceTimer);
      _advanceTimer = null;
    }
    if (_loadingTimer) {
      clearTimeout(_loadingTimer);
      _loadingTimer = null;
    }
  }
  function handleAnswer(option, index) {
    if (selectedAnswer !== null) return;
    selectedAnswer = index;
    var q = shuffledQuestions[currentQuestion];
    if (!answers[q.domain]) answers[q.domain] = [];
    answers[q.domain].push({
      qid: q.sheetId,
      score: option.score,
      text: option.text,
      questionText: q.question,
      bestText: q.options.reduce(function (a, b) {
        return a.score > b.score ? a : b;
      }).text,
    });

    var btns = document.querySelectorAll("#options-container .option-btn");
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.add("disabled");
      if (i === index) btns[i].classList.add("selected");
      else btns[i].classList.add("dimmed");
    }

    _advanceTimer = setTimeout(function () {
      _advanceTimer = null;
      if (currentQuestion < shuffledQuestions.length - 1) {
        currentQuestion++;
        if (shuffledQuestions[currentQuestion].beatBefore) {
          showBeat(shuffledQuestions[currentQuestion]);
        } else {
          renderQuestion();
        }
      } else if (appState.phase === "hook") {
        showScreen("screen-loading");
        _loadingTimer = setTimeout(function () {
          _loadingTimer = null;
          showSnapshot();
        }, 1400);
      } else {
        appState.phase = "done";
        showScreen("screen-loading");
        _loadingTimer = setTimeout(function () {
          _loadingTimer = null;
          showResults();
        }, 1800);
      }
    }, 350);
  }

  function getDomainScores() {
    var scores = {};
    for (var i = 0; i < appState.runDomainOrder.length; i++) {
      var k = appState.runDomainOrder[i],
        da = answers[k] || [];
      if (da.length === 0) {
        scores[k] = 0;
        continue;
      }
      var sum = 0;
      for (var j = 0; j < da.length; j++) sum += da[j].score;
      scores[k] = Math.round((sum / (da.length * 4)) * 100);
    }
    return scores;
  }

  function getOverallScore(scores) {
    var sum = 0,
      n = 0;
    for (var i = 0; i < appState.runDomainOrder.length; i++) {
      var da = answers[appState.runDomainOrder[i]] || [];
      for (var j = 0; j < da.length; j++) {
        sum += da[j].score;
        n++;
      }
    }
    return n ? Math.round((sum / (n * 4)) * 100) : 0;
  }

  function buildRadarSVG(scores, axesOpt) {
    var axes = axesOpt || appState.runDomainOrder;
    var size = 230,
      padding = 55,
      full = size + padding * 2;
    var cx = full / 2,
      cy = full / 2,
      r = size * 0.44;
    var levels = [25, 50, 75, 100];
    var step = (2 * Math.PI) / axes.length,
      start = -Math.PI / 2;
    var lm = axes.length >= 5 ? 1.22 : 1.28;

    function pt(i, v) {
      var a = start + i * step,
        d = (v / 100) * r;
      return { x: cx + d * Math.cos(a), y: cy + d * Math.sin(a) };
    }

    var svg =
      '<svg viewBox="0 0 ' +
      full +
      " " +
      full +
      '" width="100%" style="max-width:' +
      full +
      'px;overflow:visible">';

    for (var l = 0; l < levels.length; l++) {
      var pts = [];
      for (var i = 0; i < axes.length; i++) {
        var p = pt(i, levels[l]);
        pts.push(p.x + "," + p.y);
      }
      svg +=
        '<polygon points="' +
        pts.join(" ") +
        '" fill="none" stroke="#E5E7EB" stroke-width="' +
        (l === levels.length - 1 ? "1" : "0.5") +
        '" opacity="0.8"/>';
    }
    for (var i = 0; i < axes.length; i++) {
      var p = pt(i, 100);
      svg +=
        '<line x1="' +
        cx +
        '" y1="' +
        cy +
        '" x2="' +
        p.x +
        '" y2="' +
        p.y +
        '" stroke="#E5E7EB" stroke-width="0.5"/>';
    }

    var dpts = [];
    for (var i = 0; i < axes.length; i++)
      dpts.push(pt(i, scores[axes[i]] || 0));
    svg +=
      '<polygon points="' +
      dpts
        .map(function (p) {
          return p.x + "," + p.y;
        })
        .join(" ") +
      '" fill="rgba(6,172,56,0.15)" stroke="#048A24" stroke-width="2"/>';

    for (var i = 0; i < dpts.length; i++) {
      var col = getDomainColour(scores[axes[i]] || 0);
      svg +=
        '<circle cx="' +
        dpts[i].x +
        '" cy="' +
        dpts[i].y +
        '" r="4" fill="' +
        col +
        '" stroke="#FFF" stroke-width="2"/>';
    }

    var ff =
      'font-family="Plain,-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif"';
    for (var i = 0; i < axes.length; i++) {
      var k = axes[i],
        a = start + i * step;
      var lx = cx + r * lm * Math.cos(a),
        ly = cy + r * lm * Math.sin(a);
      var cosA = Math.cos(a);
      var anc = Math.abs(cosA) < 0.1 ? "middle" : cosA > 0 ? "start" : "end";
      if (anc === "start") lx -= 7;
      var d = getDomain(k);
      var lines = d.radarLines || d.name.split(" ");
      svg +=
        "<g onclick=\"scrollToDomain('" + k + '\')" style="cursor:pointer">';
      svg +=
        '<text x="' +
        lx +
        '" y="' +
        (ly - 6) +
        '" text-anchor="' +
        anc +
        '" fill="#191919" font-size="13" font-weight="700" ' +
        ff +
        ">" +
        esc(lines[0]) +
        "</text>";
      if (lines.length > 1) {
        svg +=
          '<text x="' +
          lx +
          '" y="' +
          (ly + 10) +
          '" text-anchor="' +
          anc +
          '" fill="#191919" font-size="13" font-weight="700" ' +
          ff +
          ">" +
          esc(lines.slice(1).join(" ")) +
          "</text>";
      }
      svg += "</g>";
    }
    svg += "</svg>";
    return svg;
  }

  function vendorNoteHTML(k) {
    var d = getDomain(k),
      dtier = appState.domainTier[k];
    if (dtier === "answers" && salesMessages[k]) {
      return (
        '<div class="detail-label detail-label-dark">How PagerDuty helps</div><div class="detail-text">' +
        linkifySales(k, salesMessages[k].body) +
        "</div>"
      );
    }
    if (dtier === "supports" && d.supportsLine) {
      return (
        '<div class="detail-label detail-label-dark">How PagerDuty helps</div><div class="detail-text">' +
        esc(d.supportsLine) +
        "</div>"
      );
    }
    return "";
  }

  function _updateStageVendor() {
    var el = document.getElementById("stage-vendor");
    if (el) el.innerHTML = expandedDomain ? vendorNoteHTML(expandedDomain) : "";
  }

  function scrollToDomain(key) {
    var card = document.getElementById("dc-" + key);
    if (!card) return;
    var isMobile =
      _resultsLayoutMobile !== null
        ? _resultsLayoutMobile
        : window.innerWidth <= 768;
    if (!isMobile) {
      var detail = document.getElementById("dd-" + key);
      if (detail && !detail.classList.contains("open")) toggleDomain(key);
      var cols = document.querySelector(".results-columns");
      if (cols) cols.scrollIntoView({ behavior: "smooth", block: "nearest" });
      return;
    }
    card.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(function () {
      var detail = document.getElementById("dd-" + key);
      if (detail && !detail.classList.contains("open")) {
        toggleDomain(key);
      }
    }, 400);
  }

  function setupFloatingPrompt(weakest) {
    var prompt = document.getElementById("floating-prompt");
    if (!prompt || !weakest) return;
    var priorityBox = document.getElementById("priority-box");
    if (!priorityBox) return;

    var observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (entry.isIntersecting) {
          prompt.classList.add("hidden");
        } else {
          var rect = priorityBox.getBoundingClientRect();
          if (rect.top > window.innerHeight) {
            prompt.classList.remove("hidden");
          } else {
            prompt.classList.add("hidden");
          }
        }
      },
      { threshold: 0 },
    );
    observer.observe(priorityBox);
  }

  function showResults() {
    showScreen("screen-results");
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);
    var sorted = appState.runDomainOrder.slice().sort(function (a, b) {
      return ds[a] - ds[b];
    });
    var h = "";

    h +=
      '<div class="card"><div class="score-header"><div><div class="score-label">Overall readiness</div>';
    h +=
      '<span class="score-big" style="color:' +
      tier.colour +
      '">' +
      overall +
      "%</span></div>";
    h +=
      '<span class="tier-badge" style="background:' +
      tier.bg +
      ";color:" +
      tier.colour +
      '">' +
      esc(tier.label) +
      "</span></div>";
    h +=
      '<div class="score-bar"><div class="score-bar-fill" style="width:' +
      overall +
      "%;background:" +
      tier.colour +
      '"></div></div>';
    h += '<div class="score-desc">' + esc(tier.description) + "</div>";
    h += '<div class="score-basis">' + esc(scoreBasisLine()) + "</div></div>";

    var weakDomains = sorted.filter(function (k) {
      return ds[k] < 76;
    });
    h +=
      '<div class="priority-box" id="priority-box"><div class="priority-title">Priority areas</div>';
    if (weakDomains.length === 0) {
      h +=
        '<div class="priority-text">No major gaps were identified from these responses. Review the detail below before your next audit or regulatory review.</div>';
    } else {
      h +=
        '<div class="priority-text">Your greatest exposure is in <strong>' +
        esc(getDomain(weakDomains[0]).name) +
        "</strong>";
      if (weakDomains.length > 1)
        h +=
          " and <strong>" + esc(getDomain(weakDomains[1]).name) + "</strong>";
      h +=
        ". " +
        (weakDomains.length === 1 ? "This should be" : "These should be") +
        " your immediate focus.</div>";
    }
    h +=
      '<div style="margin-top:10px"><a href="javascript:void(0)" onclick="segTrack(\'Button Clicked\', cellProps({cta:\'Download personalised report\'})); openLeadForm();" style="font-size:13px;color:#048A24;text-decoration:none">Download your personalised report to review or share internally &rarr;</a></div>';
    h += "</div>";

    var ctaHTML =
      '<div class="cta-title">Get your personalised report</div>' +
      '<div class="cta-desc">Your scorecard, priority fixes, the regulatory cheat sheet for your market and practitioner resources - a personalised PDF to download, keep and share internally.</div>' +
      '<button onclick="openLeadForm()" style="display:inline-block;padding:12px 24px;background:transparent;color:#FFFFFF;font-size:15px;font-weight:400;border:2px solid rgba(255,255,255,0.6);border-radius:4px;cursor:pointer;text-decoration:none;font-family:inherit;transition:all 0.2s">Download my personalised report</button>';

    h += '<div class="results-columns">';
    h +=
      '<div class="results-col-left"><div class="card"><div class="radar-label">Readiness by domain</div>';
    h += '<div class="radar-wrap">' + buildRadarSVG(ds) + "</div></div></div>";
    h += '<div class="results-col-right" id="results-stage">';
    h +=
      '<div class="radar-label mobile-domain-heading">Readiness by domain</div>';
    var isMobile = window.innerWidth <= 768;
    _resultsLayoutMobile = isMobile;
    var firstExpanded = false;
    var stageCards = "",
      stripCards = "";
    _resultsSorted = sorted.slice();
    for (var i = 0; i < sorted.length; i++) {
      var k = sorted[i],
        pct = ds[k],
        col = getDomainColour(pct),
        v = getDomainVerdict(pct),
        d = getDomain(k);
      var autoExpand = !firstExpanded && !isMobile;
      if (autoExpand) {
        firstExpanded = true;
        expandedDomain = k;
      }
      var card = "";

      card +=
        '<div class="domain-card' +
        (autoExpand ? " expanded" : "") +
        '" id="dc-' +
        k +
        '"><button class="domain-header" onclick="toggleDomain(\'' +
        k +
        "')\">";
      card +=
        '<div class="domain-header-left"><div class="domain-name-row"><span class="domain-name">' +
        esc(d.name) +
        '</span><span class="domain-sub">' +
        esc(d.subtitle) +
        "" +
        "</span></div>";
      card +=
        '<div class="domain-bar-row"><div class="domain-bar"><div class="domain-bar-fill" style="width:' +
        pct +
        "%;background:" +
        col +
        '"></div></div>';
      card +=
        '<span class="domain-pct" style="color:' +
        col +
        '">' +
        pct +
        "%</span></div></div>";
      card +=
        '<div class="domain-header-right"><span class="verdict-badge" style="background:' +
        col +
        "15;color:" +
        col +
        '">' +
        esc(v) +
        "</span>";
      card +=
        '<svg class="chevron' +
        (autoExpand ? " open" : "") +
        '" id="chev-' +
        k +
        '" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#6B7280" stroke-width="1.5"><path d="M4 6l4 4 4-4"/></svg></div>';
      card +=
        '</button><div class="domain-detail' +
        (autoExpand ? " open" : "") +
        '" id="dd-' +
        k +
        '"><div class="domain-detail-inner">';
      card +=
        '<div class="detail-section"><div class="detail-label detail-label-red">Why it matters</div><div class="detail-text">' +
        esc(d.regRisk) +
        "</div></div>";
      if (d.resource) {
        card +=
          '<div class="detail-section"><a class="resource-link" href="' +
          esc(d.resource.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          esc(d.resource.label) +
          ' <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 17" width="14" height="14" style="vertical-align:-2px;margin-left:3px"><path fill="currentColor" fill-rule="evenodd" d="M8.5.6C4.1.6.6 4.1.6 8.5c0 2.1.8 4.1 2.3 5.6 1.5 1.5 3.5 2.3 5.6 2.3 2.1 0 4.1-.8 5.6-2.3 1.5-1.5 2.3-3.5 2.3-5.6 0-2.1-.8-4.1-2.3-5.6C12.6 1.4 10.6.6 8.5.6zM2.2 8.5c0 3.5 2.8 6.3 6.3 6.3s6.3-2.8 6.3-6.3S12 2.2 8.5 2.2 2.2 5 2.2 8.5zm7.1-4v1.6H7.7V4.5h1.6zm0 3.2v4.8H7.7V7.7h1.6z" clip-rule="evenodd"/></svg></a></div>';
      }
      card +=
        '<div class="detail-section"><div class="detail-label detail-label-dark">What good looks like</div><div class="detail-text">' +
        esc(d.whatGoodLooksLike) +
        "</div></div>";
      var vn = vendorNoteHTML(k);
      if (vn)
        card += '<div class="detail-section vendor-note">' + vn + "</div>";
      card += "</div></div></div>";
      if (isMobile || autoExpand) stageCards += card;
      else stripCards += card;
    }
    h += stageCards;
    h += "</div></div>";
    h += '<div class="stage-vendor" id="stage-vendor"></div>';
    h +=
      '<div class="results-strip" id="results-strip">' + stripCards + "</div>";

    h += '<div class="cta-box">' + ctaHTML + "</div>";

    h +=
      '<div style="text-align:center;margin:12px 0 16px"><a href="https://www.pagerduty.com/contact-us/" target="_blank" rel="noopener noreferrer" onclick="segTrack(\'Button Clicked\', cellProps({cta:\'Get in touch\'}))" style="font-size:14px;color:#048A24;text-decoration:none;font-family:inherit">Get in touch &rarr;</a></div>';

    h +=
      '<p style="font-size:11px;color:#9CA3AF;line-height:1.5;text-align:center;max-width:680px;margin:0 auto 8px">' +
      esc(DISCLAIMER) +
      "</p>";

    if (weakDomains.length > 0) {
      h +=
        '<div class="floating-prompt hidden" id="floating-prompt"><button class="floating-prompt-btn" onclick="scrollToDomain(\'' +
        weakDomains[0] +
        "')\">";
      h +=
        '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2v12M4 10l4 4 4-4" stroke="#DC2626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      h += "See your priority gaps</button></div>";
    }

    document.getElementById("results-container").innerHTML = h;
    _updateStageVendor();

    if (weakDomains.length > 0) {
      setTimeout(function () {
        setupFloatingPrompt(weakDomains[0]);
      }, 100);
    }

    if (isMobile) {
      var peekKey = sorted[0];
      var peekTimer1 = setTimeout(function () {
        toggleDomain(peekKey);
      }, 400);
      var peekTimer2 = setTimeout(function () {
        toggleDomain(peekKey);
      }, 1200);
      document.querySelectorAll(".domain-header").forEach(function (btn) {
        btn.addEventListener(
          "click",
          function () {
            clearTimeout(peekTimer1);
            clearTimeout(peekTimer2);
          },
          { once: true },
        );
      });
    }
  }

  var _resultsSorted = [];
  var _resultsLayoutMobile = null;
  var _relayoutTimer = null;
  window.addEventListener("resize", function () {
    if (_resultsLayoutMobile === null) return;
    var scr = document.getElementById("screen-results");
    if (!scr || !scr.classList.contains("active")) return;
    var nowMobile = window.innerWidth <= 768;
    if (nowMobile === _resultsLayoutMobile) return;
    clearTimeout(_relayoutTimer);
    _relayoutTimer = setTimeout(function () {
      _origResults();
    }, 250);
  });

  function _stripInsert(card, key) {
    var strip = document.getElementById("results-strip");
    var order = _resultsSorted;
    var before = null;
    for (var i = order.indexOf(key) + 1; i < order.length; i++) {
      var el = document.getElementById("dc-" + order[i]);
      if (el && el.parentElement === strip) {
        before = el;
        break;
      }
    }
    strip.insertBefore(card, before);
  }

  function _collapseCard(key, noTransition) {
    var pd = document.getElementById("dd-" + key);
    var pc = document.getElementById("dc-" + key);
    var pv = document.getElementById("chev-" + key);
    if (noTransition && pd) pd.classList.add("no-transition");
    if (pd) pd.classList.remove("open");
    if (pc) pc.classList.remove("expanded");
    if (pv) pv.classList.remove("open");
    if (noTransition)
      setTimeout(function () {
        if (pd) pd.classList.remove("no-transition");
      }, 20);
  }

  function toggleDomain(key) {
    var isMobile =
      _resultsLayoutMobile !== null
        ? _resultsLayoutMobile
        : window.innerWidth <= 768;
    var card = document.getElementById("dc-" + key);
    var detail = document.getElementById("dd-" + key);
    var chev = document.getElementById("chev-" + key);
    var isOpen = detail.classList.contains("open");

    if (isMobile) {
      if (expandedDomain && expandedDomain !== key && !isOpen)
        _collapseCard(expandedDomain, true);
      else if (expandedDomain && expandedDomain !== key)
        _collapseCard(expandedDomain, false);
      if (isOpen) {
        _collapseCard(key, false);
        expandedDomain = null;
      } else {
        detail.classList.add("open");
        card.classList.add("expanded");
        chev.classList.add("open");
        expandedDomain = key;
      }
      return;
    }

    var stage = document.getElementById("results-stage");
    if (isOpen) {
      return;
    } else {
      if (expandedDomain && expandedDomain !== key) {
        var prev = document.getElementById("dc-" + expandedDomain);
        _collapseCard(expandedDomain, true);
        if (prev) _stripInsert(prev, expandedDomain);
      }
      stage.appendChild(card);
      detail.classList.add("open");
      card.classList.add("expanded");
      chev.classList.add("open");
      expandedDomain = key;
      _updateStageVendor();
    }
  }

  function segTrack(event, props) {
    if (DEBUG) {
      debugLog(
        "segment " +
          event +
          (props && props.event_type ? "/" + props.event_type : ""),
        props || {},
      );
      return;
    }
    getSegment("track")(event, props || {});
  }

  var _origStart = startAssessment;
  startAssessment = function () {
    segTrack(
      "Generic Event",
      cellProps({ event_type: "cta_clicked", cta: "Start the assessment" }),
    );
    _origStart();
  };

  var _origHandle = handleAnswer;
  handleAnswer = function (option, index) {
    if (selectedAnswer !== null) return;
    var q = shuffledQuestions[currentQuestion];
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_answer_selected",
        question_number: currentQuestion + 1,
        question_id: "q" + q.sheetId,
        domain: q.domain,
        tier: q.tier,
        phase: appState.phase,
        score: option.score,
        answer_text: option.text,
      }),
    );
    _origHandle(option, index);
  };

  var _origSnapshot = showSnapshot;
  showSnapshot = function () {
    _origSnapshot();
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);
    appState.hookDurationSeconds = Math.round(
      (Date.now() - appState.startedAt) / 1000,
    );
    var props = cellProps({
      event_type: "assessment_hook_complete",
      overall_score: overall,
      tier: tier.label,
      questions_answered: appState.hookTotal,
      duration_seconds: appState.hookDurationSeconds,
      run_id: appState.runId,
    });
    appState.runDomainOrder.forEach(function (k) {
      props["score_" + k] = ds[k];
    });
    if (appState.utm && appState.utm.utm_campaign)
      props.utm_campaign = appState.utm.utm_campaign;
    if (appState.utm && appState.utm.utm_source)
      props.utm_source = appState.utm.utm_source;
    segTrack("Generic Event", props);
    try {
      sendToSheets(buildSheetsPayload("hook"));
    } catch (e) {}
  };

  var _origContinueFull = continueToFullQuiz;
  continueToFullQuiz = function () {
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_fullquiz_accepted",
        run_id: appState.runId,
      }),
    );
    _origContinueFull();
  };

  function buildSheetsPayload(stage) {
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);
    var byQid = {};
    for (var qk in answers) {
      var qa = answers[qk];
      for (var qi = 0; qi < qa.length; qi++) byQid[qa[qi].qid] = qa[qi];
    }
    var q = function (id) {
      return byQid[id] ? byQid[id].score : "";
    };
    var qa2 = function (id) {
      return byQid[id] ? byQid[id].text : "";
    };
    var s = function (slug) {
      return appState.runDomainOrder.indexOf(slug) >= 0 ? ds[slug] : "";
    };

    var gsPayload = {
      timestamp: new Date().toISOString(),
      overall: overall,
      tier: tier.label,
      cps230: appState.cell === "au-fsi" ? ds.ops : "",
      cps234: s("cps234"),
      soci: s("soci"),
      privacy: s("privacy"),
    };
    [1, 2, 3, 11, 13, 15, 4, 5, 12, 6, 16, 8, 9].forEach(function (id) {
      gsPayload["q" + id] = q(id);
      gsPayload["q" + id + "_answer"] = qa2(id);
    });
    gsPayload.geo = appState.geo || "";
    gsPayload.industry = appState.industry || "";
    gsPayload.apra = apraClass();
    [17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29].forEach(function (id) {
      gsPayload["q" + id] = q(id);
      gsPayload["q" + id + "_answer"] = qa2(id);
    });
    ["ops", "security", "svc", "peak", "cust", "ai"].forEach(function (slug) {
      gsPayload["score_" + slug] =
        slug === "ops" && appState.cell === "au-fsi" ? "" : s(slug);
    });
    gsPayload.cell = appState.cell || "";
    gsPayload.degraded = appState.degraded;
    gsPayload.qualifier = appState.qualifier || "";
    gsPayload.duration_seconds =
      stage === "hook"
        ? appState.hookDurationSeconds || 0
        : appState.durationSeconds || 0;
    gsPayload.stage = stage;
    gsPayload.run_id = appState.runId || "";
    gsPayload.geo_source = appState.geoSource || "";
    gsPayload.ai_focus = appState.focusAI ? "yes" : "";
    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ].forEach(function (k) {
      gsPayload[k] = (appState.utm && appState.utm[k]) || "";
    });
    return gsPayload;
  }

  function sendToSheets(gsPayload) {
    if (DEBUG && !DEBUG_SHEETS_LIVE) {
      debugLog("sheets suppressed", gsPayload);
      return;
    }
    if (DEBUG) debugLog("sheets SENT", gsPayload);
    fetch(SHEETS_ENDPOINT, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(gsPayload),
    }).catch(function () {});
  }

  var _origResults = showResults;
  showResults = function () {
    _origResults();
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);
    if (!appState.durationSeconds && appState.startedAt) {
      appState.durationSeconds = Math.round(
        (Date.now() - appState.startedAt) / 1000,
      );
    }
    var completeProps = cellProps({
      event_type: "assessment_complete_success",
      overall_score: overall,
      tier: tier.label,
      duration_seconds: appState.durationSeconds || 0,
      run_id: appState.runId,
    });
    appState.runDomainOrder.forEach(function (k) {
      completeProps["score_" + k] = ds[k];
    });
    if (appState.utm && appState.utm.utm_campaign)
      completeProps.utm_campaign = appState.utm.utm_campaign;
    if (appState.utm && appState.utm.utm_source)
      completeProps.utm_source = appState.utm.utm_source;
    segTrack("Generic Event", completeProps);
    try {
      sendToSheets(buildSheetsPayload("full"));
    } catch (e) {}
  };

  var _engagementFired = false;

  var GEO_COUNTRY = {
    au: "Australia",
    sg: "Singapore",
    nz: "New Zealand",
    hk: "Hong Kong",
    my: "Malaysia",
    th: "Thailand",
    kr: "Republic of Korea",
  };
  var _countryPrefilled = false;

  var PHONE_HINT = {
    au: "+61 4xx xxx xxx",
    sg: "+65 9xxx xxxx",
    nz: "+64 21 xxx xxxx",
    hk: "+852 9xxx xxxx",
    my: "+60 12 xxx xxxx",
    th: "+66 8x xxx xxxx",
    kr: "+82 10 xxxx xxxx",
  };
  function openLeadForm() {
    var contactLabel = document.getElementById("lead-optin-contact-text");
    if (contactLabel)
      contactLabel.textContent = fill(
        "Speak with PagerDuty about my results and what other {PEER_NOUN} are doing.",
      );
    if (!_countryPrefilled) {
      _countryPrefilled = true;
      document.getElementById("Form_Country__c").value =
        GEO_COUNTRY[appState.geo] || "";
      document.getElementById("Phone").placeholder =
        PHONE_HINT[appState.geo] || "+xx xxx xxx xxx";
    }
    if (DEBUG) debugPrefillLeadForm();
    showScreen("screen-leadform");
    requestAnimationFrame(function () {
      window.scrollTo(0, 0);
    });
    if (!_engagementFired) {
      _engagementFired = true;
      segTrack("Marketo Form Engagement Detected", cellProps());
    }
  }

  function goBackToResults() {
    document.getElementById("lead-step-1").style.display = "block";
    document.getElementById("lead-step-2").style.display = "none";
    if (
      _resultsLayoutMobile !== null &&
      window.innerWidth <= 768 !== _resultsLayoutMobile
    ) {
      _origResults();
    } else {
      showScreen("screen-results");
    }
  }

  var titlePrefixes = [
    "dr",
    "dr.",
    "doc",
    "doc.",
    "doctor",
    "prof",
    "prof.",
    "professor",
    "sir",
    "dame",
    "mr",
    "mr.",
    "mrs",
    "mrs.",
    "ms",
    "ms.",
    "miss",
    "mx",
    "mx.",
    "rev",
    "rev.",
    "reverend",
    "hon",
    "hon.",
    "honourable",
    "honorable",
    "lord",
    "lady",
    "capt",
    "capt.",
    "captain",
    "maj",
    "maj.",
    "major",
    "col",
    "col.",
    "colonel",
    "sgt",
    "sgt.",
    "sergeant",
  ];

  function parseName(fullName) {
    fullName = fullName.trim();
    var commaIdx = fullName.indexOf(",");
    if (commaIdx !== -1) {
      fullName = (
        fullName.slice(commaIdx + 1) +
        " " +
        fullName.slice(0, commaIdx)
      ).trim();
    }
    var parts = fullName.split(/\s+/);
    while (parts.length > 1) {
      var candidate = parts[0].toLowerCase().replace(/\.$/, "");
      if (titlePrefixes.indexOf(candidate) !== -1) {
        parts.shift();
      } else {
        break;
      }
    }
    if (parts.length === 0) return { first: "", last: "" };
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts[0], last: parts[parts.length - 1] };
  }

  function goToLeadStep2() {
    clearFieldErrors();
    var fullName = document.getElementById("FullName").value.trim();
    var email = document.getElementById("Email").value.trim();
    var optinMarketingEl = document.querySelector(
      'input[name="internationalMarketingOptIn"]:checked',
    );
    var valid = true;
    if (!fullName) {
      showFieldError("FullName");
      valid = false;
    }
    if (!email || !isWorkEmail(email)) {
      showFieldError("Email");
      valid = false;
    }
    if (!optinMarketingEl) {
      showFieldError("internationalMarketingOptIn");
      valid = false;
    }
    if (!valid) {
      focusFirstError();
      return;
    }

    var parsed = parseName(fullName);
    document.getElementById("FirstName").value = parsed.first;
    document.getElementById("LastName").value = parsed.last;

    var greeting = parsed.first
      ? "Almost there, " + parsed.first
      : "Almost there";
    document.getElementById("step2-title").textContent = greeting;

    document.getElementById("lead-step-1").style.display = "none";
    document.getElementById("lead-step-2").style.display = "block";
    window.scrollTo(0, 0);
    segTrack("Marketo Form Input Detected", cellProps());
  }

  function togglePhoneField() {
    var show = document.getElementById("lead-optin-contact").checked;
    document.getElementById("phone-field").style.display = show
      ? "block"
      : "none";
    if (!show) document.getElementById("Phone").value = "";
  }

  var personalEmailDomains = [
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "yahoo.co.uk",
    "yahoo.com.au",
    "yahoo.fr",
    "yahoo.de",
    "ymail.com",
    "hotmail.com",
    "hotmail.co.uk",
    "hotmail.fr",
    "hotmail.de",
    "outlook.com",
    "outlook.co.uk",
    "outlook.fr",
    "outlook.de",
    "live.com",
    "msn.com",
    "aol.com",
    "aim.com",
    "icloud.com",
    "me.com",
    "mac.com",
    "mail.com",
    "gmx.com",
    "gmx.net",
    "gmx.de",
    "web.de",
    "t-online.de",
    "protonmail.com",
    "proton.me",
    "pm.me",
    "tutanota.com",
    "tuta.io",
    "mailfence.com",
    "disroot.org",
    "zoho.com",
    "yandex.com",
    "yandex.ru",
    "fastmail.com",
    "fastmail.fm",
    "hey.com",
    "duck.com",
    "qq.com",
    "163.com",
    "126.com",
    "sina.com",
    "foxmail.com",
  ];

  function isWorkEmail(email) {
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) return false;
    var parts = email.split("@");
    if (parts.length !== 2) return false;
    var domain = parts[1].toLowerCase();
    if (/\.\./.test(domain) || /^-|-$/.test(domain)) return false;
    for (var i = 0; i < personalEmailDomains.length; i++) {
      if (domain === personalEmailDomains[i]) return false;
    }
    return true;
  }

  function isValidPhone(phone) {
    var digits = phone.replace(/[^0-9]/g, "");
    return digits.length >= 4;
  }

  function showFieldError(id) {
    var el = document.getElementById(id);
    var err = document.getElementById("err-" + id);
    if (el) el.classList.add("field-error");
    if (err) err.style.display = "block";
  }
  function clearFieldErrors() {
    var els = document.querySelectorAll(".field-error");
    for (var i = 0; i < els.length; i++) els[i].classList.remove("field-error");
    var errs = document.querySelectorAll(".lead-form-error");
    for (var i = 0; i < errs.length; i++) errs[i].style.display = "none";
  }
  function focusFirstError() {
    var firstError = document.querySelector("#screen-leadform .field-error");
    if (!firstError) return;
    var target =
      firstError.tagName === "INPUT" || firstError.tagName === "SELECT"
        ? firstError
        : firstError.querySelector("input, select");
    if (target && typeof target.focus === "function") target.focus();
  }
  function clearErrorFor(id) {
    var el = document.getElementById(id);
    var err = document.getElementById("err-" + id);
    if (el) el.classList.remove("field-error");
    if (err) err.style.display = "none";
  }
  function setupLeadFormBehaviour() {
    var fieldIds = [
      "FullName",
      "Email",
      "Form_Country__c",
      "FirstName",
      "LastName",
      "Phone",
      "Title",
      "Company",
    ];
    fieldIds.forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      var clear = function () {
        clearErrorFor(id);
      };
      el.addEventListener("input", clear);
      el.addEventListener("change", clear);
    });
    var radios = document.querySelectorAll(
      'input[name="internationalMarketingOptIn"]',
    );
    radios.forEach(function (r) {
      r.addEventListener("change", function () {
        clearErrorFor("internationalMarketingOptIn");
      });
    });
    function enterHandler(advanceFn) {
      return function (e) {
        if (e.key !== "Enter") return;
        var t = e.target;
        if (
          t &&
          (t.tagName === "BUTTON" ||
            t.tagName === "TEXTAREA" ||
            t.tagName === "A" ||
            t.tagName === "SELECT")
        )
          return;
        e.preventDefault();
        advanceFn();
      };
    }
    var step1 = document.getElementById("lead-step-1");
    var step2 = document.getElementById("lead-step-2");
    if (step1) step1.addEventListener("keydown", enterHandler(goToLeadStep2));
    if (step2) step2.addEventListener("keydown", enterHandler(submitLeadForm));
  }

  var _leadSubmitting = false;
  function submitLeadForm() {
    if (!window.jspdf) {
      alert(
        "Hang tight, your report is still loading. Please try again in a few seconds.",
      );
      return;
    }
    var turnstileForm =
      window.pd && window.pd.turnstileForms
        ? window.pd.turnstileForms["lead-step-2"]
        : null;
    if (!turnstileForm || !turnstileForm.isReady) return;
    if (_leadSubmitting) return;

    clearFieldErrors();
    var firstName = document.getElementById("FirstName").value.trim();
    var lastName = document.getElementById("LastName").value.trim();
    var email = document.getElementById("Email").value.trim();
    var phone = document.getElementById("Phone").value.trim();
    var title = document.getElementById("Title").value.trim();
    var company = document.getElementById("Company").value.trim();
    var country = document.getElementById("Form_Country__c").value;
    var optinMarketingEl = document.querySelector(
      'input[name="internationalMarketingOptIn"]:checked',
    );
    var optinMarketing = optinMarketingEl ? optinMarketingEl.value : "";
    var optinContact = document.getElementById("lead-optin-contact").checked;

    var valid = true;
    if (!firstName) {
      showFieldError("FirstName");
      valid = false;
    }
    if (!lastName) {
      showFieldError("LastName");
      valid = false;
    }
    if (optinContact && (!phone || !isValidPhone(phone))) {
      showFieldError("Phone");
      valid = false;
    }
    if (!title) {
      showFieldError("Title");
      valid = false;
    }
    if (!company) {
      showFieldError("Company");
      valid = false;
    }
    if (!valid) {
      focusFirstError();
      return;
    }

    var btn = document.getElementById("lead-submit");
    _leadSubmitting = true;
    btn.disabled = true;
    btn.textContent = "Generating...";

    var fullName = firstName + " " + lastName;
    var ds = getDomainScores(),
      overall = getOverallScore(ds),
      tier = getTier(overall);

    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_downloaded",
        overall_score: overall,
        tier: tier.label,
        lead_country: country,
        optin_marketing: optinMarketing,
        optin_contact: optinContact,
      }),
    );
    segTrack("Marketo Form Submit", cellProps());

    try {
      var commentLines = [buildResultsLink()];

      var mktoFields = {
        programName: mktoDest().programName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
        Title: title,
        Company: company,
        Form_Country__c: country,
        LeadSource: LEAD_SOURCE,
        internationalMarketingOptIn: optinMarketing,
        checkboxtobecontacted: optinContact,
        Web_Comments__c: commentLines.join("\n### "),
        "cf-turnstile-response":
          (document.querySelector('input[name="cf-turnstile-response"]') || {})
            .value || "",
      };

      if (DEBUG) {
        debugLog("marketo suppressed", mktoFields);
      } else {
        fetch(mktoDest().endpoint, {
          method: "POST",
          body: new URLSearchParams(mktoFields),
        }).catch(function () {});
      }
    } catch (e) {}

    setTimeout(function () {
      generatePDFjspdf(fullName, ds, overall, tier);
      _leadSubmitting = false;
      btn.disabled = false;
      btn.textContent = "Get my report";
      populateConfirmation();
      showScreen("screen-confirmation");
      window.scrollTo(0, 0);
      segTrack(
        "Generic Event",
        cellProps({
          event_type: "assessment_confirmation",
          variant: optinContact ? "contact" : "no-contact",
        }),
      );
    }, 300);
  }

  function reopenLeadForm() {
    var firstName = document.getElementById("FirstName").value.trim();
    var lastName = document.getElementById("LastName").value.trim();
    var email = document.getElementById("Email").value.trim();
    if (firstName && lastName && email && window.jspdf) {
      var ds = getDomainScores(),
        overall = getOverallScore(ds),
        tier = getTier(overall);
      generatePDFjspdf(firstName + " " + lastName, ds, overall, tier);
      segTrack(
        "Generic Event",
        cellProps({ event_type: "assessment_redownload" }),
      );
    } else {
      openLeadForm();
    }
  }

  function populateConfirmation() {
    var el = document.getElementById("confirmation-content");
    var optinContact = document.getElementById("lead-optin-contact").checked;
    var conf =
      (appState.cell !== "default" && CONFIRM_BY_CELL[appState.cell]) ||
      CONFIRM_BY_CELL["other-" + appState.industry] ||
      CONFIRM_BY_CELL["default"];
    var h = "";

    h += '<div class="confirmation-header">';
    h +=
      '<div class="confirmation-title">Your personalised report is ready</div>';
    h +=
      '<button class="confirmation-again" onclick="reopenLeadForm()">Download again</button>';
    h += '<div class="confirmation-desc">' + esc(conf.sub) + "</div>";
    h += "</div>";

    h += '<div class="confirm-section-label">Recommended next steps</div>';
    var tileCount = conf.tiles.length + (optinContact ? 0 : 1);
    h +=
      '<div class="confirm-tiles' +
      (tileCount === 3 ? " three-col" : "") +
      '">';

    for (var t = 0; t < conf.tiles.length; t++) {
      var tile = conf.tiles[t];
      h += '<div class="confirm-resource">';
      h += '<div class="confirm-resource-title">' + esc(tile.title) + "</div>";
      h += '<div class="confirm-resource-desc">' + esc(tile.desc) + "</div>";
      h +=
        '<a class="confirm-resource-link" href="' +
        esc(tile.url) +
        '" target="_blank" rel="noopener noreferrer" onclick="segTrack(\'Button Clicked\', cellProps({cta:\'' +
        tile.cta +
        "'}))\">" +
        esc(tile.cta) +
        " &rarr;</a>";
      h += "</div>";
    }

    if (!optinContact) {
      h += '<div class="confirm-resource">';
      h +=
        '<div class="confirm-resource-title">' +
        esc(conf.contact.title) +
        "</div>";
      h +=
        '<div class="confirm-resource-desc">' +
        esc(fill(conf.contact.desc)) +
        "</div>";
      h +=
        '<a class="confirm-resource-link" href="' +
        esc(conf.contact.url) +
        '" target="_blank" rel="noopener noreferrer" onclick="segTrack(\'Button Clicked\', cellProps({cta:\'Get in touch\'}))">Get in touch &rarr;</a>';
      h += "</div>";
    }

    h += "</div>"; // close confirm-tiles

    el.innerHTML = h;
  }

  var salesMessages = {
    ops: {
      title: "Operational resilience that stands up to scrutiny",
      body: "PagerDuty gives you visibility of incidents affecting critical operations, escalates automatically when an alert goes unacknowledged, and records the incident timeline and response activity.",
    },
    security: {
      title: "Detection and response built for tight reporting windows",
      body: "PagerDuty correlates and deduplicates security alerts, routes them to the right responder directly, and provides structured notification workflows to support fast, structured reporting.",
    },
    cps234: {
      title: "Detection and response built for tight reporting windows",
      body: "PagerDuty correlates and deduplicates security alerts, routes them to the right responder directly, and provides structured notification workflows to support the 72-hour reporting window.",
    },
    soci: {
      title: "Critical infrastructure coverage, around the clock",
      body: "PagerDuty automates on-call scheduling across time zones, shows you where out-of-hours coverage has gaps, and routes critical incidents down the escalation paths you define - so the ACSC report starts from a timestamped timeline.",
    },
    privacy: {
      title: "Breach response that runs in parallel",
      body: "PagerDuty pulls security, legal and communications into the same incident from a single declaration, can kick off your containment runbooks automatically, and keeps stakeholder notification templates ready to send.",
    },
    ai: {
      title: "When AI misbehaves, the clock still runs",
      body: "PagerDuty treats AI failure like any other operational incident: it surfaces the warning signs your monitoring raises, escalates straight to a named human owner, and keeps a timestamped record of who took over and when.",
    },
    svc: {
      title: "Network incidents with the regulator watching",
      body: "PagerDuty turns service-impact alerts from your network monitoring into rehearsed escalation across time zones and out-of-hours rosters, timestamping response steps - so the report to the regulator starts from the incident record.",
    },
    peak: {
      title: "Protect your biggest trading hours",
      body: "PagerDuty lets you tune alerting for your biggest trading hours, escalates automatically as soon as your monitoring flags trouble, and correlates provider signals with your own telemetry so a third-party fault shows up as exactly that.",
    },
    cust: {
      title: "Evidence built for your customers' auditors",
      body: "PagerDuty keeps a timestamped, attributable record of each incident - who was paged, who responded, what was done - that you can pull per customer through its API and analytics, with status pages and stakeholder updates built into the response.",
    },
    generic: {
      title: "Resilience you can show",
      body: "PagerDuty brings detection, escalation, response and evidence onto one platform - so when something breaks, the right people are working on it quickly, and the record is there afterwards.",
    },
  };

  var KB = "https://support.pagerduty.com/main/docs/";
  var SALES_KB_LINKS = {
    ops: [
      { phrase: "escalates automatically", slug: "escalation-policies" },
      { phrase: "audit trail", slug: "audit-trail-reporting" },
    ],
    security: [
      { phrase: "correlates and deduplicates", slug: "alert-grouping" },
      { phrase: "notification workflows", slug: "incident-workflows" },
    ],
    cps234: [
      { phrase: "correlates and deduplicates", slug: "alert-grouping" },
      { phrase: "notification workflows", slug: "incident-workflows" },
    ],
    soci: [
      {
        phrase: "on-call scheduling across time zones",
        slug: "shift-based-schedules",
      },
      { phrase: "escalation paths", slug: "escalation-policies" },
    ],
    svc: [{ phrase: "out-of-hours rosters", slug: "shift-based-schedules" }],
    peak: [{ phrase: "escalates automatically", slug: "escalation-policies" }],
    cust: [
      {
        phrase: "timestamped, attributable record",
        slug: "audit-trail-reporting",
      },
    ],
    ai: [
      {
        phrase: "escalates straight to a named human owner",
        slug: "escalation-policies",
      },
    ],
  };
  function linkifySales(key, text) {
    var html = esc(text);
    (SALES_KB_LINKS[key] || []).forEach(function (l) {
      var marked = esc(l.phrase);
      html = html.replace(
        marked,
        '<a class="kb-link" href="' +
          KB +
          l.slug +
          '" target="_blank" rel="noopener noreferrer" onclick="segTrack(\'Button Clicked\', cellProps({cta:\'kb:' +
          l.slug +
          "'}))\">" +
          marked +
          "</a>",
      );
    });
    return html;
  }

  var INDUSTRY_PHRASE = {
    fsi: "financial services",
    telco: "telecommunications",
    ecom: "retail and ecommerce",
    digital: "technology and digital services",
  };
  function pdfScopePhrase() {
    var ind = INDUSTRY_PHRASE[(appState.industry || "").replace("-lite", "")];
    var geoName =
      appState.geo && appState.geo !== "other" ? GEO_NAMES[appState.geo] : null;
    if (ind && geoName) return ind + " in " + geoName;
    if (geoName) return "organisations in " + geoName;
    if (ind) return ind;
    return "";
  }

  var ABOUT_PAGERDUTY =
    "PagerDuty, Inc. (NYSE: PD) is the global leader in AI-first digital operations. By automatically detecting, diagnosing, and remediating issues, the PagerDuty Platform orchestrates AI agents and automated workflows with context from over 750 integrations. Trusted by approximately two-thirds of the Fortune 100 and nearly half of the Fortune 500, PagerDuty is the industry standard for organisations scaling resilient, autonomous operations.";

  var PDF_UTM_SOURCE = "pdf";
  var PDF_UTM_MEDIUM = "pdf";
  var PDF_UTM_CAMPAIGN = "apra";
  function pdfUtm(url) {
    if (url.indexOf("pagerduty.com") === -1) return url;
    return (
      url +
      (url.indexOf("?") === -1 ? "?" : "&") +
      "utm_source=" +
      PDF_UTM_SOURCE +
      "&utm_medium=" +
      PDF_UTM_MEDIUM +
      "&utm_campaign=" +
      PDF_UTM_CAMPAIGN
    );
  }

  function buildCheatRows() {
    var rows = [];
    for (var i = 0; i < appState.runDomainOrder.length; i++) {
      var k = appState.runDomainOrder[i],
        d = getDomain(k);
      if (d.deadline)
        rows.push({
          name: d.name,
          line: d.deadline,
          url: d.resource && d.resource.url,
        });
    }
    var s = SLOTS[appState.cell] || SLOTS["default"];
    if (s.CYBER_AGENCY) {
      var mentioned = rows.some(function (r) {
        return r.line.indexOf(s.CYBER_AGENCY) >= 0;
      });
      if (!mentioned)
        rows.push({
          name: "Critical incident reporting",
          line:
            s.CYBER_LINE ||
            "Report to " + s.CYBER_AGENCY + " within " + s.CYBER_CLOCK + ".",
        });
    }
    return rows;
  }

  function pdfLabel(doc, label, x, yy, col) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    if (col) doc.setTextColor(col.r, col.g, col.b);
    else doc.setTextColor(4, 138, 36);
    doc.text(label, x, yy);
  }

  var PDF_LOGO_B64 =
    "data:image/jpeg;base64,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";
  function generatePDFjspdf(userName, scores, overall, tier) {
    var jsPDF = window.jspdf.jsPDF;
    var doc = new jsPDF({ unit: "mm", format: "a4" });
    var w = 210,
      pg = 297,
      ml = 20,
      mr = 20,
      cw = w - ml - mr;
    var y = 0;
    var pdfMeta = pdfMetaFor();
    var sorted = appState.runDomainOrder.slice().sort(function (a, b) {
      return scores[a] - scores[b];
    });
    var dateStr = new Date().toLocaleDateString("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    var answersTier = sorted.filter(function (k) {
      return appState.domainTier[k] === "answers";
    });
    var weakestKey = answersTier.length ? answersTier[0] : sorted[0];
    var sales = salesMessages[weakestKey] || salesMessages.generic;
    function hex(c) {
      return {
        r: parseInt(c.slice(1, 3), 16),
        g: parseInt(c.slice(3, 5), 16),
        b: parseInt(c.slice(5, 7), 16),
      };
    }
    var tc = hex(tier.colour);

    var weakByDomain = {};
    for (var di = 0; di < sorted.length; di++) {
      var dk = sorted[di],
        wa = answers[dk] || [];
      var wq = [];
      for (var qi = 0; qi < wa.length; qi++) {
        if (wa[qi].score < 4) wq.push(wa[qi]);
      }
      if (wq.length > 0) weakByDomain[dk] = wq;
    }

    function needsPage(h) {
      if (y + h > pg - 10) {
        doc.addPage();
        y = 15;
        return true;
      }
      return false;
    }

    doc.setFillColor(4, 138, 36);
    doc.rect(0, 0, w, 24, "F");
    try {
      doc.addImage(PDF_LOGO_B64, "JPEG", ml, 7, 35, 10);
    } catch (e) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text("PagerDuty", ml, 14);
    }
    doc.link(ml, 7, 35, 10, { url: pdfUtm(pdfMeta.url) });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(pdfMeta.title, w - mr, 11, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(200, 230, 210);
    var pdfCompany = (document.getElementById("Company") || {}).value || "";
    doc.text(
      userName + (pdfCompany ? ", " + pdfCompany.trim() : "") + " | " + dateStr,
      w - mr,
      16,
      { align: "right" },
    );

    y = 46;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(40);
    doc.setTextColor(tc.r, tc.g, tc.b);
    doc.text(overall + "%", ml, y);
    var scoreW = doc.getTextWidth(overall + "%");
    doc.setFontSize(13);
    doc.text(tier.label, ml + scoreW + 6, y - 8);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(74, 74, 74);
    var descL = doc.splitTextToSize(tier.description, cw - scoreW - 12);
    doc.text(descL, ml + scoreW + 6, y - 2);
    var scope = pdfScopePhrase();
    if (scope) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(107, 114, 128);
      doc.text(
        "Assessed against the obligations that apply to " + scope + ".",
        ml,
        y + 8,
      );
    }
    y += 20;

    for (var bi = 0; bi < appState.runDomainOrder.length; bi++) {
      var bk = appState.runDomainOrder[bi],
        bpct = scores[bk],
        bcol = hex(getDomainColour(bpct));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(25, 25, 25);
      doc.text(getDomain(bk).name, ml, y + 5);
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(ml + 46, y, cw - 68, 7, 2, 2, "F");
      doc.setFillColor(bcol.r, bcol.g, bcol.b);
      var barW = ((cw - 68) * bpct) / 100;
      if (barW > 4) doc.roundedRect(ml + 46, y, barW, 7, 2, 2, "F");
      else if (barW > 0) doc.rect(ml + 46, y, barW, 7, "F");
      doc.setTextColor(bcol.r, bcol.g, bcol.b);
      doc.text(bpct + "%", ml + cw - 15, y + 5);
      y += 12;
    }
    y += 3;
    doc.setDrawColor(229, 231, 235);
    doc.line(ml, y, w - mr, y);
    y += 6;

    var hasDomains = sorted.filter(function (k) {
      return weakByDomain[k];
    });
    var pdfWeak = sorted.filter(function (k) {
      return scores[k] < 76;
    });
    var focusLine;
    if (pdfWeak.length === 0) {
      focusLine =
        "No major gaps were identified from these responses. Review the detail below before your next audit or regulatory review.";
      if (hasDomains.length > 0)
        focusLine += " Page 2 lists the answers that still have headroom.";
    } else {
      focusLine =
        "Your greatest exposure is in " +
        getDomain(pdfWeak[0]).name +
        " (" +
        scores[pdfWeak[0]] +
        "%)";
      if (pdfWeak.length > 1)
        focusLine +=
          ", then " +
          getDomain(pdfWeak[1]).name +
          " (" +
          scores[pdfWeak[1]] +
          "%)";
      focusLine += ". Page 2 shows the specific answers to work on.";
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(74, 74, 74);
    var focusL = doc.splitTextToSize(focusLine, cw);
    doc.text(focusL, ml, y);
    y += focusL.length * 3.6 + 8;

    function drawInvestigate() {
      var bx = ml + 3.4,
        indentX = ml + 12,
        calloutX = indentX + 8;
      var MEASURE = 125; // ~3/4 of the page width, as the guide sets it
      var BAND_H = 26,
        BOT = pg - 18;

      var TYPE = {
        qf: 9.5,
        qlh: 4.9,
        bf: 8.5,
        blh: 4.4,
        nameF: 12.5,
        dlF: 7.5,
        dlLH: 3.9,
      };
      var GAPS = {
        headGap: 7.5,
        barGap: 7.5,
        dlGap: 7,
        bpGap: 2,
        qGap: 9,
        dGap: 16,
        firstGap: 24,
        contGap: 8,
      };

      var S_MIN = 72,
        S_MAX = 125,
        S_COMFORT = 88,
        MAX_PAGES = 3;

      function gapsAt(s100) {
        var s = s100 / 100,
          g = {};
        for (var k in GAPS) if (GAPS.hasOwnProperty(k)) g[k] = GAPS[k] * s;
        g.firstY = BAND_H + g.firstGap;
        return g;
      }

      var prevLHF = doc.getLineHeightFactor ? doc.getLineHeightFactor() : 1.15;
      doc.setLineHeightFactor(1.46);

      function splitHanging(text, firstW, restW) {
        var all = doc.splitTextToSize(text, firstW);
        if (all.length <= 1) return all;
        var rest = text.slice(all[0].length).replace(/^\s+/, "");
        return [all[0]].concat(doc.splitTextToSize(rest, restW));
      }

      function measureQ(q) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(TYPE.qf);
        var ql = doc.splitTextToSize(q.questionText, MEASURE);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(TYPE.bf);
        var prefixW = doc.getTextWidth("Best practice: ");
        doc.setFont("helvetica", "normal");
        var bpL = splitHanging(q.bestText, MEASURE - 8 - prefixW, MEASURE - 8);
        return { ql: ql, bpL: bpL, prefixW: prefixW };
      }
      function qH(m, g) {
        return (
          m.ql.length * TYPE.qlh +
          g.bpGap +
          (m.bpL.length - 1) * TYPE.blh +
          g.qGap
        );
      }
      function headH(pb, g) {
        return (
          g.headGap +
          g.barGap +
          (pb.dl ? (pb.dl.length - 1) * TYPE.dlLH + g.dlGap : 2)
        );
      }

      var plan = hasDomains.map(function (wk2) {
        var wd2 = getDomain(wk2),
          dl = null;
        if (wd2.deadline) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(TYPE.dlF);
          dl = doc.splitTextToSize(wd2.deadline, MEASURE);
        }
        var qs = weakByDomain[wk2].slice().sort(function (a, b) {
          return a.score - b.score;
        });
        return { key: wk2, dom: wd2, dl: dl, qm: qs.map(measureQ) };
      });

      function pagesNeeded(g) {
        var n = 1,
          yy = g.firstY;
        for (var i = 0; i < plan.length; i++) {
          var pb = plan[i],
            hh = headH(pb, g);
          if (i > 0) yy += g.dGap;
          if (i > 0 && yy + hh + (pb.qm.length ? qH(pb.qm[0], g) : 0) > BOT) {
            n++;
            yy = 22;
          }
          yy += hh;
          for (var j = 0; j < pb.qm.length; j++) {
            var qhj = qH(pb.qm[j], g);
            if (j > 0 && yy + qhj > BOT) {
              n++;
              yy = 22 + g.contGap;
            }
            yy += qhj;
          }
        }
        return n;
      }

      var chosen = null;
      for (var np = 1; np <= MAX_PAGES && !chosen; np++) {
        for (var si = S_MAX; si >= S_MIN; si--) {
          var gc = gapsAt(si);
          if (pagesNeeded(gc) <= np) {
            if (si >= S_COMFORT || np === MAX_PAGES) chosen = gc;
            break;
          }
        }
      }
      var trimmed = false;
      if (!chosen) {
        chosen = gapsAt(S_MIN);
        while (pagesNeeded(chosen) > MAX_PAGES) {
          var fat = -1,
            most = 1;
          for (var pi2 = 0; pi2 < plan.length; pi2++) {
            if (plan[pi2].qm.length > most) {
              most = plan[pi2].qm.length;
              fat = pi2;
            }
          }
          if (fat < 0) break; // one answer each and still over - let it run long
          plan[fat].qm.pop();
          trimmed = true;
        }
      }
      var G = chosen;

      doc.addPage();
      doc.setFillColor(4, 138, 36);
      doc.rect(0, 0, w, BAND_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("Areas to investigate", ml, 13);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(222, 242, 228);
      doc.text(
        trimmed
          ? "Your widest gaps in each area, to work on before your next audit or regulatory review."
          : "Based on your answers - what to work on before your next audit or regulatory review.",
        ml,
        19,
      );
      y = G.firstY;

      for (var wi = 0; wi < plan.length; wi++) {
        var pl = plan[wi],
          wk = pl.key,
          wd = pl.dom,
          qm = pl.qm,
          dlL = pl.dl;

        if (wi > 0 && y + headH(pl, G) + (qm.length ? qH(qm[0], G) : 0) > BOT) {
          doc.addPage();
          y = 22;
        }

        doc.setFillColor(4, 138, 36);
        doc.circle(bx, y - 1.3, 3.4, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text(String(wi + 1), bx, y + 0.1, { align: "center" });

        var dcol = hex(getDomainColour(scores[wk]));
        doc.setFont("helvetica", "bold");
        doc.setFontSize(TYPE.nameF);
        doc.setTextColor(25, 25, 25);
        if (wd.resource && wd.resource.url)
          doc.textWithLink(wd.name, indentX, y, {
            url: pdfUtm(wd.resource.url),
          });
        else doc.text(wd.name, indentX, y);
        var nw = doc.getTextWidth(wd.name);
        doc.setFontSize(10.5);
        doc.setTextColor(dcol.r, dcol.g, dcol.b);
        doc.text(scores[wk] + "%", indentX + nw + 4, y);
        y += G.headGap;

        doc.setFillColor(240, 240, 240);
        doc.roundedRect(indentX, y - 3.2, 54, 4.4, 1.5, 1.5, "F");
        doc.setFillColor(dcol.r, dcol.g, dcol.b);
        var dW = (54 * scores[wk]) / 100;
        if (dW > 3) doc.roundedRect(indentX, y - 3.2, dW, 4.4, 1.5, 1.5, "F");
        else if (dW > 0) doc.rect(indentX, y - 3.2, dW, 4.4, "F");
        y += G.barGap;

        if (dlL) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(TYPE.dlF);
          doc.setTextColor(107, 114, 128);
          doc.text(dlL, indentX, y);
          y += (dlL.length - 1) * TYPE.dlLH + G.dlGap;
        } else {
          y += 2;
        }

        for (var qi = 0; qi < qm.length; qi++) {
          var ql = qm[qi].ql,
            bpL = qm[qi].bpL;
          if (qi > 0 && y + qH(qm[qi], G) > BOT) {
            doc.addPage();
            y = 22;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor(140, 140, 140);
            doc.text(wd.name + " continued", indentX, y);
            y += G.contGap;
          }

          doc.setFont("helvetica", "normal");
          doc.setFontSize(TYPE.qf);
          doc.setTextColor(25, 25, 25);
          doc.text(ql, indentX, y);
          y += ql.length * TYPE.qlh + G.bpGap;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(TYPE.bf);
          doc.setTextColor(4, 138, 36);
          doc.text("Best practice: ", calloutX, y);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(74, 74, 74);
          doc.text(bpL[0], calloutX + qm[qi].prefixW, y);
          for (var li = 1; li < bpL.length; li++) {
            y += TYPE.blh;
            doc.text(bpL[li], calloutX, y);
          }
          y += G.qGap;
        }
        y += G.dGap;
      }
      doc.setLineHeightFactor(prevLHF);
    }

    var cheatRows = buildCheatRows();
    if (cheatRows.length > 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      var rowParts = cheatRows.map(function (cr) {
        var lineL = doc.splitTextToSize(cr.line, cw - 74);
        var nameL = doc.splitTextToSize(cr.name, 50);
        return {
          lineL: lineL,
          nameL: nameL,
          url: cr.url,
          ch: Math.max(lineL.length, nameL.length) * 3.8,
        };
      });
      var cheatScope = pdfScopePhrase();
      var cheatSub =
        "The reporting obligations " +
        (cheatScope ? "for " + cheatScope : "this check covered") +
        ", in one place. Worth pinning up near the incident channel.";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      var cheatSubL = doc.splitTextToSize(cheatSub, cw - 14);
      var rowsH = rowParts.reduce(function (a, r) {
        return a + r.ch;
      }, 0);

      var PAD_T = 8,
        GAP_TITLE = 5.5,
        GAP_SUB = 7,
        PAD_B = 4,
        ROW_GAP = 3.5;
      var cardH =
        PAD_T +
        GAP_TITLE +
        GAP_SUB +
        (cheatSubL.length - 1) * 3.8 +
        rowsH -
        3.8 +
        ROW_GAP * (rowParts.length - 1) +
        PAD_B;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      var slackSales =
        doc.splitTextToSize(sales.body, cw - 16).length * 3.6 + 27;
      doc.setFontSize(6);
      var slackDisc =
        doc.splitTextToSize(scoreBasisLine() + "  " + DISCLAIMER, cw).length *
        2.7;
      var slack = 285 - slackDisc - slackSales - 8 - (y + cardH + 8);
      if (slack > 24) y += Math.min(slack / 3, 26);

      doc.setFillColor(246, 248, 247);
      doc.roundedRect(ml, y, cw, cardH, 2, 2, "F");
      var cy = y + PAD_T;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(25, 25, 25);
      doc.text("The clocks that apply to you", ml + 7, cy);
      cy += GAP_TITLE;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(cheatSubL, ml + 7, cy);
      cy += GAP_SUB + (cheatSubL.length - 1) * 3.8;
      for (var cri = 0; cri < rowParts.length; cri++) {
        var rp = rowParts[cri];
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(4, 138, 36);
        if (rp.url) {
          for (var nli = 0; nli < rp.nameL.length; nli++)
            doc.textWithLink(rp.nameL[nli], ml + 7, cy + nli * 3.8, {
              url: pdfUtm(rp.url),
            });
        } else {
          doc.text(rp.nameL, ml + 7, cy);
        }
        doc.setFont("helvetica", "normal");
        doc.setTextColor(74, 74, 74);
        doc.text(rp.lineL, ml + 60, cy);
        cy += rp.ch + (cri < rowParts.length - 1 ? ROW_GAP : 0);
      }
      y += cardH + 8;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    var sL = doc.splitTextToSize(sales.body, cw - 16);
    var salesH = sL.length * 3.6 + 27;

    doc.setFontSize(6);
    var discL = doc.splitTextToSize(scoreBasisLine() + "  " + DISCLAIMER, cw);
    var discTop = 285 - discL.length * 2.7;
    var salesTop = discTop - salesH - 8;
    if (salesTop >= y) {
      y = salesTop;
    } else if (y + salesH + discL.length * 2.7 + 10 > 290) {
      doc.addPage();
      y = 18;
      discTop = y + salesH + 6;
    } else {
      discTop = y + salesH + 6;
    }

    doc.setFillColor(4, 138, 36);
    doc.roundedRect(ml, y, cw, salesH, 1.5, 1.5, "F");
    pdfLabel(doc, "How PagerDuty helps", ml + 8, y + 7.5, {
      r: 190,
      g: 235,
      b: 200,
    });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(255, 255, 255);
    doc.text(sales.title, ml + 8, y + 13.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(sL, ml + 8, y + 19.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    var linkText = "Find out more at " + pdfMeta.linkLabel;
    var linkX = ml + 8,
      linkY = y + salesH - 5;
    doc.textWithLink(linkText, linkX, linkY, { url: pdfUtm(pdfMeta.url) });
    var linkW = doc.getTextWidth(linkText);
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.2);
    doc.line(linkX, linkY + 0.8, linkX + linkW, linkY + 0.8);

    y += salesH;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(156, 163, 175);
    doc.text(discL, ml, discTop);

    if (hasDomains.length > 0) drawInvestigate();

    var kbSlugs = [],
      kbSeen = {};
    appState.runDomainOrder.forEach(function (dk2) {
      (SALES_KB_LINKS[dk2] || []).forEach(function (l) {
        if (!kbSeen[l.slug]) {
          kbSeen[l.slug] = true;
          kbSlugs.push(l.slug);
        }
      });
    });
    var KB_META = {
      "escalation-policies": [
        "Escalation policies",
        "Automatic escalation when a responder misses an alert.",
      ],
      "audit-trail-reporting": [
        "Audit trail reporting",
        "Timestamped, attributable records for evidence.",
      ],
      "alert-grouping": [
        "Alert grouping",
        "Correlate and deduplicate noisy alerts into one incident.",
      ],
      "incident-workflows": [
        "Incident workflows",
        "Structured notification and mobilisation steps.",
      ],
      "shift-based-schedules": [
        "Shift-based schedules",
        "On-call coverage across time zones and rosters.",
      ],
    };
    var kbRows = kbSlugs.filter(function (s2) {
      return KB_META[s2];
    });
    var conf2 =
      (appState.cell !== "default" && CONFIRM_BY_CELL[appState.cell]) ||
      CONFIRM_BY_CELL["other-" + appState.industry] ||
      CONFIRM_BY_CELL["default"];
    var confTiles = (conf2 && conf2.tiles) || [];
    if (kbRows.length || confTiles.length) {
      doc.addPage();
      doc.setFillColor(4, 138, 36);
      doc.rect(0, 0, w, 26, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("Where to go next", ml, 13);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(222, 242, 228);
      doc.text(
        "Documentation for the steps above, and further reading.",
        ml,
        19,
      );
      y = 38;
      if (kbRows.length) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(25, 25, 25);
        doc.text("Set this up in PagerDuty", ml, y);
        y += 9;
        kbRows.forEach(function (slug) {
          var m = KB_META[slug];
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(4, 138, 36);
          doc.textWithLink(m[0], ml, y, { url: pdfUtm(KB + slug) });
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8.5);
          doc.setTextColor(74, 74, 74);
          doc.text(m[1], ml + 55, y);
          doc.setFontSize(7.5);
          doc.setTextColor(156, 163, 175);
          doc.textWithLink(
            "support.pagerduty.com/main/docs/" + slug,
            ml + 55,
            y + 4.6,
            { url: pdfUtm(KB + slug) },
          );
          y += 14;
        });
      }
      if (confTiles.length) {
        y += 6;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(25, 25, 25);
        doc.text("More reading", ml, y);
        y += 8;

        var GAP2 = 6,
          COLW = (cw - GAP2) / 2,
          CPX = 6,
          CPY = 7;
        var TLH = 4.6,
          DLH = 3.9,
          CTAH = 6;
        function measureTile(t2, iw) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          var tt = doc.splitTextToSize(t2.title, iw);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          var td = doc.splitTextToSize(t2.desc, iw);
          return {
            tt: tt,
            td: td,
            h: CPY + tt.length * TLH + 1.5 + td.length * DLH + CTAH + CPY,
          };
        }
        for (var ti = 0; ti < confTiles.length; ti += 2) {
          var rowT = confTiles.slice(ti, ti + 2);
          var cardW = rowT.length === 1 ? cw : COLW;
          var rowM = rowT.map(function (t2) {
            return measureTile(t2, cardW - CPX * 2);
          });
          var rowH = rowM.reduce(function (a, r) {
            return Math.max(a, r.h);
          }, 0);
          if (y + rowH > pg - 18) {
            doc.addPage();
            y = 22;
          }
          for (var ci = 0; ci < rowT.length; ci++) {
            var t2 = rowT[ci],
              tm = rowM[ci],
              cx = ml + ci * (COLW + GAP2);
            doc.setDrawColor(226, 229, 233);
            doc.setLineWidth(0.4);
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(cx, y, cardW, rowH, 2, 2, "FD");
            doc.setLineWidth(0.2);

            var ty = y + CPY + 3.2;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(25, 25, 25);
            for (var tli = 0; tli < tm.tt.length; tli++)
              doc.text(tm.tt[tli], cx + CPX, ty + tli * TLH);
            ty += tm.tt.length * TLH + 1.5;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(74, 74, 74);
            for (var dli = 0; dli < tm.td.length; dli++)
              doc.text(tm.td[dli], cx + CPX, ty + dli * DLH);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.setTextColor(4, 138, 36);
            doc.textWithLink(t2.cta || "Read more", cx + CPX, y + rowH - CPY, {
              url: pdfUtm(t2.url),
            });
          }
          y += rowH + GAP2;
        }
      }
    }

    var pageCount = doc.getNumberOfPages();
    for (var pgi = 1; pgi <= pageCount; pgi++) {
      doc.setPage(pgi);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(178, 183, 190);
      doc.text(String(pgi), w - mr, 292, { align: "right" });
    }

    doc.addPage();
    doc.setFillColor(4, 138, 36);
    doc.rect(0, 0, w, pg, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text("About PagerDuty", ml, 202);
    var ABOUT_Y = 209,
      ABOUT_PT = 9;
    doc.setLineHeightFactor(1.45);
    var ABOUT_LH = (ABOUT_PT * 1.45 * 25.4) / 72;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(ABOUT_PT);
    doc.setTextColor(183, 222, 193);
    var aboutL = doc.splitTextToSize(
      ABOUT_PAGERDUTY + " Learn more and try it for free at",
      128,
    );
    doc.text(aboutL, ml, ABOUT_Y);
    var lastL = aboutL[aboutL.length - 1];
    var runW = doc.getTextWidth(lastL) + doc.getTextWidth(" ");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(214, 238, 222);
    var ux = ml,
      uy = ABOUT_Y + aboutL.length * ABOUT_LH;
    if (runW + doc.getTextWidth("www.pagerduty.com") <= 128) {
      ux = ml + runW;
      uy = ABOUT_Y + (aboutL.length - 1) * ABOUT_LH;
    }
    doc.textWithLink("www.pagerduty.com", ux, uy, {
      url: pdfUtm("https://www.pagerduty.com/"),
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(214, 238, 222);
    doc.text("© " + new Date().getFullYear() + " PagerDuty, Inc.", ml, 288);
    try {
      doc.addImage(PDF_LOGO_B64, "JPEG", w - mr - 35, 274, 35, 10);
    } catch (e) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text("PagerDuty", w - mr, 282, { align: "right" });
    }

    doc.save(pdfMeta.filename);
  }

  var TZ_GEO = {
    "Asia/Singapore": "sg",
    "Pacific/Auckland": "nz",
    "Pacific/Chatham": "nz",
    "Asia/Hong_Kong": "hk",
    "Asia/Kuala_Lumpur": "my",
    "Asia/Kuching": "my",
    "Asia/Bangkok": "th",
    "Asia/Seoul": "kr",
  };
  function guessGeoFromTz() {
    try {
      var tz =
        DEBUG && appState.params.tz
          ? appState.params.tz
          : Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz) return null;
      if (
        DEBUG &&
        own(GEO_ALIASES, tz.toLowerCase()) &&
        own(GEO_ALIASES, tz.toLowerCase()) !== "other"
      )
        return own(GEO_ALIASES, tz.toLowerCase());
      if (tz.indexOf("Australia/") === 0) return "au";
      return TZ_GEO[tz] || null;
    } catch (e) {
      return null;
    }
  }

  function regionLineMode() {
    if (appState.geoSource === "switched") return "back-au";
    if (appState.geoSource === "default") {
      return guessGeoFromTz() !== DEFAULT_GEO ? "to-global" : null;
    }
    if (!appState.geo || appState.geo === "other" || !GEO_NAMES[appState.geo])
      return null;
    if (appState.geoSource === "detected") return "confirm";
    if (appState.geoSource !== "param") return null;
    var guess = guessGeoFromTz();
    if (!(guess && guess !== appState.geo && CELLS[guess + "-generic"]))
      return null;
    return appState.showPicker ? "confirm" : "to-global";
  }

  function needsRegionConfirm() {
    return regionLineMode() === "confirm";
  }

  function _regionLink(fn, label) {
    return (
      '<a href="javascript:void(0)" onclick="' +
      fn +
      '" style="color:#9CA3AF;text-decoration:underline">' +
      label +
      "</a>"
    );
  }

  function regionLineHTML(from) {
    var mode = regionLineMode();
    if (mode === "to-global") {
      var name = esc(GEO_NAMES[appState.geo] || GEO_NAMES[DEFAULT_GEO]);
      return (
        "Mapped to " +
        name +
        "'s rules - " +
        _regionLink(
          "switchToGlobal('" + from + "')",
          "not there? switch to the global check",
        )
      );
    }
    if (mode === "back-au") {
      return (
        "Mapped to general good practice - " +
        _regionLink(
          "switchToDefaultGeo('" + from + "')",
          "back to " + esc(GEO_NAMES[DEFAULT_GEO]) + "'s rules",
        )
      );
    }
    var fn =
      from === "quiz"
        ? "correctRegionFromQuiz()"
        : from === "setup"
          ? "correctRegionFromSetup()"
          : "correctRegion()";
    return (
      "Region: " +
      esc(GEO_NAMES[appState.geo]) +
      (appState.geoSource === "detected"
        ? " (detected from your device)"
        : "") +
      " - " +
      _regionLink(fn, "click here if this is wrong")
    );
  }

  function updateRegionLine() {
    var el = document.getElementById("geo-region");
    if (!el) return;
    if (regionLineMode()) {
      el.innerHTML = regionLineHTML("home");
      el.style.display = "block";
    } else {
      el.style.display = "none";
    }
  }

  function updateQuestionRegionLine() {
    var el = document.getElementById("question-region");
    if (!el) return;
    if (regionLineMode() && appState.phase === "hook" && currentQuestion < 2) {
      el.innerHTML = regionLineHTML("quiz");
      el.style.display = "block";
    } else {
      el.style.display = "none";
    }
  }

  function clearRegionSelection() {
    if (appState.showPicker) {
      appState.geo = null;
      appState.geoSource = "";
    } else {
      appState.geo = DEFAULT_GEO;
      appState.geoSource = "default";
    }
    appState.cellLocked = false;
    appState.degraded = false;
    appState.qualifier = "";
    _domainCache = {};
  }

  function switchToGlobal(from) {
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_geo_switched",
        to: "other",
        from: from,
      }),
    );
    _switchGeo("other", "switched", from);
  }

  function switchToDefaultGeo(from) {
    segTrack(
      "Generic Event",
      cellProps({
        event_type: "assessment_geo_switched",
        to: DEFAULT_GEO,
        from: from,
      }),
    );
    _switchGeo(DEFAULT_GEO, "default", from);
  }

  function _switchGeo(geo, source, from) {
    var ind = appState.industry;
    appState.cellLocked = false;
    appState.qualifier = "";
    appState.degraded = false;
    _domainCache = {};
    if (ind && ind !== "other") {
      resolveCell(ind, geo);
      appState.industry = ind;
    } else {
      appState.geo = geo;
      appState.cell = CELLS[geo + "-generic"] ? geo + "-generic" : "default";
    }
    appState.geoSource = source;
    if (from === "quiz") {
      clearAdvanceTimers();
      answers = {};
      currentQuestion = 0;
      selectedAnswer = null;
      renderDebugPanel();
      if (needsSetupSteps()) {
        nextSetupStep();
        return;
      }
      beginScenarios();
      return;
    }
    renderDebugPanel();
    if (from === "setup") {
      nextSetupStep();
      return;
    }
    stopSampleWidget();
    renderHome();
  }

  function correctRegion() {
    segTrack(
      "Generic Event",
      cellProps({ event_type: "assessment_geo_corrected", from: "home" }),
    );
    clearRegionSelection();
    if (appState.industry && appState.industry !== "other") {
      appState.route = "params-partial";
      appState.cell = "default";
    } else {
      appState.route = "cold";
      appState.industry = null;
      appState.cell = "default";
    }
    stopSampleWidget();
    nextSetupStep();
  }

  function correctRegionFromSetup() {
    segTrack(
      "Generic Event",
      cellProps({ event_type: "assessment_geo_corrected", from: "setup" }),
    );
    clearRegionSelection();
    nextSetupStep();
  }

  function correctRegionFromQuiz() {
    segTrack(
      "Generic Event",
      cellProps({ event_type: "assessment_geo_corrected", from: "quiz" }),
    );
    clearAdvanceTimers();
    clearRegionSelection();
    answers = {};
    currentQuestion = 0;
    selectedAnswer = null;
    nextSetupStep();
  }

  document.addEventListener("DOMContentLoaded", function () {
    var stuckFor = 0;
    var watchdog = setInterval(function () {
      var step2 = document.getElementById("lead-step-2");
      var btn = document.getElementById("lead-submit");
      if (!step2 || !btn) {
        clearInterval(watchdog);
        return;
      }
      if (step2.style.display === "none" || !step2.offsetParent) {
        stuckFor = 0;
        return;
      }
      if (!btn.disabled) {
        clearInterval(watchdog);
        var h = document.getElementById("turnstile-hint");
        if (h) h.parentNode.removeChild(h);
        return;
      }
      stuckFor++;
      if (stuckFor >= 10 && !document.getElementById("turnstile-hint")) {
        var hint = document.createElement("div");
        hint.id = "turnstile-hint";
        hint.style.cssText =
          "font-size:13px;color:#6B7280;text-align:center;margin-top:8px;";
        hint.textContent =
          "The security check is taking a while to load. If the button stays disabled, check your connection and refresh the page.";
        btn.parentNode.insertBefore(hint, btn.nextSibling);
      }
    }, 1000);
  });

  window.startAssessment = startAssessment;
  window.goToLeadStep2 = goToLeadStep2;
  window.submitLeadForm = submitLeadForm;
  window.toggleDomain = toggleDomain;
  window.scrollToDomain = scrollToDomain;
  window.openLeadForm = openLeadForm;
  window.goBackToResults = goBackToResults;
  window.reopenLeadForm = reopenLeadForm;
  window.togglePhoneField = togglePhoneField;
  window.segTrack = segTrack;
  window.cellProps = cellProps;
  window.chooseGeo = chooseGeo;
  window.chooseIndustry = chooseIndustry;
  window.chooseCell = chooseCell;
  window.resetSelections = resetSelections;
  window.proceedFromBeat = proceedFromBeat;
  window.continueToFullQuiz = continueToFullQuiz;
  window.correctRegion = correctRegion;
  window.correctRegionFromSetup = correctRegionFromSetup;
  window.correctRegionFromQuiz = correctRegionFromQuiz;
  window.switchToGlobal = switchToGlobal;
  window.switchToDefaultGeo = switchToDefaultGeo;

  document.addEventListener("DOMContentLoaded", function () {
    setupLeadFormBehaviour();
    (function () {
      var p = new URLSearchParams(window.location.search);
      var rawInd = p.get("industry") || p.get("sector") || p.get("vertical");
      var rawGeo = p.get("geo") || p.get("country") || p.get("market");
      var rawCell = (p.get("cell") || "").toLowerCase();
      appState.params = {
        industry: rawInd,
        geo: rawGeo,
        cell: rawCell || null,
        tz: p.get("tz"),
        ai: p.get("ai"),
      };
      appState.focusAI = /^(yes|1|true)$/i.test(p.get("ai") || "");
      appState.utm = {};
      [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
      ].forEach(function (k) {
        var v = p.get(k);
        if (v) appState.utm[k] = v.slice(0, 200);
      });
      var ind = normaliseIndustry(rawInd),
        geo = normaliseGeo(rawGeo);
      if (/^(all|pick|picker)$/.test(String(rawGeo || "").toLowerCase())) {
        appState.showPicker = true;
        geo = null;
      }

      if (rawCell && own(CELLS, rawCell) && rawCell !== "default") {
        appState.route = "params";
        appState.geoSource = "param";
        appState.cell = rawCell;
        appState.geo = CELLS[rawCell].geo;
        appState.industry = rawCell
          .slice(CELLS[rawCell].geo.length + 1)
          .replace(/-lite$/, "");
        appState.degraded = false;
        appState.cellLocked = true;
      } else if (ind && geo) {
        appState.route = "params";
        appState.geoSource = "param";
        if (ind === "fsi-lite") {
          if (geo === "au") {
            appState.industry = "fsi";
            appState.geo = "au";
            appState.degraded = false;
            appState.cell = "au-fsi-lite";
            appState.cellLocked = true;
          } else {
            resolveCell("fsi", geo);
            appState.cellLocked = true;
          }
        } else if (ind === "digital-lite") {
          if (geo === "au") {
            appState.industry = "digital";
            appState.geo = "au";
            appState.degraded = false;
            appState.cell = "au-digital-lite";
            appState.cellLocked = true;
          } else {
            resolveCell("digital", geo);
            appState.cellLocked = true;
          }
        } else {
          resolveCell(ind, geo);
          appState.cellLocked = !(
            geo === "au" &&
            ind === "fsi" &&
            String(rawInd).toLowerCase() !== "apra"
          );
        }
      } else if (geo && !ind) {
        appState.route = "params-partial";
        appState.geoSource = "param";
        appState.geo = geo;
        appState.cell = CELLS[geo + "-generic"] ? geo + "-generic" : "default";
      } else if (ind && !geo) {
        var guess1 = appState.showPicker ? guessGeoFromTz() : DEFAULT_GEO;
        if (guess1 && CELLS[guess1 + "-generic"]) {
          appState.route = "params";
          appState.geoSource = appState.showPicker ? "detected" : "default";
          var baseInd = ind.replace(/-lite$/, "");
          if (/-lite$/.test(ind) && guess1 === "au" && CELLS["au-" + ind]) {
            appState.industry = baseInd;
            appState.geo = "au";
            appState.degraded = false;
            appState.cell = "au-" + ind;
            appState.cellLocked = true;
          } else {
            resolveCell(baseInd, guess1);
            appState.cellLocked = !(
              guess1 === "au" &&
              baseInd === "fsi" &&
              String(rawInd).toLowerCase() !== "apra"
            );
          }
        } else {
          appState.route = "params-partial";
          appState.industry = ind;
          appState.cell = "default";
        }
      } else if (appState.showPicker) {
        var guess2 = guessGeoFromTz();
        if (guess2 && CELLS[guess2 + "-generic"]) {
          appState.route = "params-partial";
          appState.geoSource = "detected";
          appState.geo = guess2;
          appState.cell = guess2 + "-generic";
        } else {
          appState.route = "cold";
          appState.cell = "default";
        }
      } else {
        appState.route = "params-partial";
        appState.geoSource = "default";
        appState.geo = DEFAULT_GEO;
        appState.cell = CELLS[DEFAULT_GEO + "-generic"]
          ? DEFAULT_GEO + "-generic"
          : "default";
      }
      renderHome();
      if (appState.geoSource === "detected") {
        segTrack(
          "Generic Event",
          cellProps({
            event_type: "assessment_geo_detected",
            detected_geo: appState.geo,
          }),
        );
      } else if (appState.geoSource === "default") {
        segTrack(
          "Generic Event",
          cellProps({
            event_type: "assessment_geo_assumed",
            detected_geo: guessGeoFromTz() || "",
          }),
        );
      } else if (needsRegionConfirm()) {
        segTrack(
          "Generic Event",
          cellProps({
            event_type: "assessment_geo_mismatch",
            detected_geo: guessGeoFromTz(),
          }),
        );
      }
    })();
  });
})();
